import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import ConfirmationModal from '../components/ConfirmationModal';
import { menuService, dishService, specialNameService, menuTypeService, getCourses, getMenuTypeCourses } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { useSchool } from '../contexts/SchoolContext';
import LoadingScreen from '../components/LoadingScreen';
import DeleteMenuModal from '../components/DeleteMenuModal';
import { toast } from 'react-hot-toast';
import { containsIgnoringAccents } from '../utils/stringUtils';
import { getAdminUrl, getMenuEditorUrl } from '../utils/urlUtils';
import { useSelector } from 'react-redux';
import { useQueryClient, useQuery } from '@tanstack/react-query';

// Import React Query hooks
import { useMenuTypes } from '../hooks/useMenuTypes';
import { useDishes, useCreateDish } from '../hooks/useDishes';
import { useCourses } from '../hooks/useCourses';
import { useMenuTypeCourses } from '../hooks/useMenuTypeCourses';
import { useMenu, useCreateMenu, useUpdateMenu } from '../hooks/useMenu';
import { useSpecialNames, useCreateSpecialName } from '../hooks/useSpecialNames';
import { 
  useMenuItemsForDay, 
  useDeleteMenuItem, 
  useSetMenuItemDish,
  useClearMenuItemDish
} from '../hooks/useMenuItems';
import CopyMenuModal from '../components/CopyMenuModal';

// Re-enable all imports - Step 5a
import ThreeDotsDropdown from '../components/common/ThreeDotsDropdown';
import NotesModal from '../components/modals/NotesModal';
import CommentsModal from '../components/modals/CommentsModal';
import CopyWeekModal from '../components/modals/CopyWeekModal';
import CopyDayModal from '../components/modals/CopyDayModal';

// Add a style element for the fadeIn animation
const notificationStyle = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeOut {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-10px); }
  }
`;

// All Tailwind colors at 50 lightness arranged to avoid similar adjacent colors
const COURSE_COLORS_ARRAY = [
  'bg-blue-50',    // Blue
  'bg-amber-50',   // Amber (orangey yellow)
  'bg-pink-50',    // Pink
  'bg-emerald-50', // Emerald green
  'bg-violet-50',  // Violet
  'bg-red-50',     // Red
  'bg-teal-50',    // Teal
  'bg-yellow-50',  // Yellow
  'bg-indigo-50',  // Indigo
  'bg-orange-50',  // Orange
  'bg-green-50',   // Green
  'bg-purple-50',  // Purple
  'bg-cyan-50',    // Cyan
  'bg-rose-50',    // Rose
  'bg-lime-50',    // Lime
  'bg-fuchsia-50', // Fuchsia
  'bg-sky-50',     // Sky blue
  'bg-slate-50'    // Slate
];

// Function to get color based on display_order
const getCourseColor = (course) => {
  // Use display_order if available, otherwise fall back to course ID
  const colorIndex = (course.display_order !== undefined ? course.display_order - 1 : course.id - 1) % COURSE_COLORS_ARRAY.length;
  return COURSE_COLORS_ARRAY[colorIndex >= 0 ? colorIndex : 0];
};

const MenuEditor = () => {
  const { user: contextUser } = useUser(); // Get user from context for authorization
  const { school } = useSchool(); // Add school context for branding colors
  const { t, i18n } = useTranslation();
  const { menuId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!menuId;

  // --- Log Component Start ---
  console.log(`[MenuEditor Render] Start. isEditing=${isEditing}, menuId=${menuId}, path=${location.pathname}, search=${location.search}`);
  // --- End Log ---

  const autocompleteRefs = useRef({});
  const specialNameAutocompleteRefs = useRef({});
  const dishInputRefs = useRef({});
  const specialNameInputRefs = useRef({});
  // Add our new refs for dropdown containers here at the top level
  const specialNameDropdownRefs = useRef({});
  const dishDropdownRefs = useRef({});
  
  // eslint-disable-next-line no-unused-vars
  const formRef = useRef(null);
  const coursesRef = useRef(null); // Reference to store courses data cache and track language changes
  const lastFetchedMenuTypeIdRef = useRef(null); // Track last fetched menu type ID to prevent infinite calls
  
  // Access the query client
  const queryClient = useQueryClient();
  
  // Get current school ID from Redux store - this is set by SchoolContext based on subdomain
  const currentSchoolId = useSelector(state => state.language.currentSchoolId);
  
  // Add React Query hook to check if menus exist for the school (for guidance banner)
  const { 
    data: menusData = [], 
    isLoading: isMenusLoading
  } = useQuery({
    queryKey: ['menus', currentSchoolId],
    queryFn: async () => {
      if (!currentSchoolId) return [];
      const response = await fetch(`/api/admin/menus?schoolId=${currentSchoolId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch menus');
      }
      return response.json();
    },
    enabled: !!currentSchoolId
  });
  
  // States
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // Add state for success notification
  const [isNotificationVisible, setIsNotificationVisible] = useState(false); // Control notification visibility
  const [notificationAnimation, setNotificationAnimation] = useState('fadeIn'); // Animation state
  const [user, setUser] = useState(null); // Restore user state for backward compatibility
  const [specialNames, setSpecialNames] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState({});
  const [autocompleteResults, setAutocompleteResults] = useState({});
  const [showSpecialNameAutocomplete, setShowSpecialNameAutocomplete] = useState({});
  const [specialNameAutocompleteResults, setSpecialNameAutocompleteResults] = useState({});
  const [originalFormData, setOriginalFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    dayNumber: null,
    courseId: null,
    dayId: null,
    isSpecialName: false
  });
  const [defaultLanguage, setDefaultLanguage] = useState(null); // Store school's default language code
  
  // Add state for tracking focused autocomplete items
  const [focusedDishIndex, setFocusedDishIndex] = useState({});
  const [focusedSpecialNameIndex, setFocusedSpecialNameIndex] = useState({});
  
  // Add the missing isDeleteModalOpen state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  // State for copy menu modal
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  
  // Re-enable modal state variables - Step 2
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [isCopyWeekModalOpen, setIsCopyWeekModalOpen] = useState(false);
  const [selectedDayIdForModal, setSelectedDayIdForModal] = useState(null);
  const [currentNoteText, setCurrentNoteText] = useState('');
  
  // State for copy week functionality
  const [sourceWeekDays, setSourceWeekDays] = useState([]);
  
  // State for copy day functionality
  const [isCopyDayModalOpen, setIsCopyDayModalOpen] = useState(false);
  const [sourceDayData, setSourceDayData] = useState(null);
  
  // Get initial month and menuTypeId from URL if available
  const urlParams = new URLSearchParams(location.search);
  const urlYear = urlParams.get('year');
  const urlMonth = urlParams.get('month');
  const urlMenuTypeId = urlParams.get('menuTypeId');
  
  // Format the month from URL params if available
  const initialMonth = urlYear && urlMonth 
    ? `${urlYear}-${urlMonth.padStart(2, '0')}` 
    : new Date().toISOString().slice(0, 7); // Default to current month
  
  // Menu form state
  const [formData, setFormData] = useState({
    month: initialMonth, 
    menuTypeId: urlMenuTypeId ? parseInt(urlMenuTypeId, 10) : null, // Will be set to school's default or lowest available if null
    days: []
  });

  // --- Log Initial State ---
  console.log(`[MenuEditor Render] Initial formData state: month=${formData.month}, menuTypeId=${formData.menuTypeId}, from URL params=${!!urlYear && !!urlMonth}`);
  // --- End Log ---
  
  // Reference to track if we've already applied location state or query params
  const locationStateAppliedRef = useRef(false);
  
  // Helper function to get the appropriate school ID
  const getSchoolId = useCallback(() => {
    // Priority 1: Use the school ID from Redux store (set by SchoolContext based on subdomain)
    if (currentSchoolId) {
      return currentSchoolId;
    }
    // Priority 2: Use the school from SchoolContext
    if (school?.id) {
      return school.id;
    }
    // Priority 3: Fall back to user's current school (for backward compatibility)
    if (user?.current_school?.id) {
      return user.current_school.id;
    }
    return null;
  }, [currentSchoolId, school, user]);
  
  // Use React Query hooks
  const schoolId = getSchoolId();
  const language = i18n.language || 'en';
  
  // Track whether we've loaded dishes to implement progressive loading
  const [dishesLoaded, setDishesLoaded] = useState(false);
  
  // Replace direct API calls with React Query hooks - implement progressive loading
  const { data: menuTypesData, isLoading: isMenuTypesLoading, isFetching: isMenuTypesFetching } = useMenuTypes(schoolId);
  const [menuTypes, setMenuTypes] = useState([]);
  
  // Only show loading screen for initial load, not background refetches
  const isMenuTypesInitialLoading = isMenuTypesLoading && !isMenuTypesFetching;
  
  // Only fetch dishes when needed - progressive loading implementation
  const { data: dishesData, isLoading: isDishesLoading, isFetching: isDishesFetching } = useDishes(schoolId, {
    // Only enable the query when editing an existing menu or when dishes are explicitly needed
    enabled: dishesLoaded || isEditing || !!formData.menuTypeId,
  });
  
  const [dishes, setDishes] = useState([]);
  // Only show loading screen for initial dish load, not background refetches
  const isDishesInitialLoading = isDishesLoading && !isDishesFetching;
  
  const { data: coursesData, isLoading: isCoursesLoading, isFetching: isCoursesFetching } = useCourses(schoolId);
  // Only show loading screen for initial courses load, not background refetches
  const isCoursesInitialLoading = isCoursesLoading && !isCoursesFetching;
  
  const { data: menuTypeCoursesData, isLoading: isMenuTypeCoursesLoading, isFetching: isMenuTypeCoursesFetching } = 
    useMenuTypeCourses(formData.menuTypeId);
  // Only show loading screen for initial menu type courses load, not background refetches
  const isMenuTypeCoursesInitialLoading = isMenuTypeCoursesLoading && !isMenuTypeCoursesFetching;
  
  const { data: menuData, isLoading: isMenuLoading, isFetching: isMenuFetching } = 
    useMenu(isEditing ? menuId : null, language);
  // Only show loading screen for initial menu load, not background refetches
  const isMenuInitialLoading = isMenuLoading && !isMenuFetching;
  
  const { data: specialNamesData, isLoading: isSpecialNamesLoading, isFetching: isSpecialNamesFetching } = 
    useSpecialNames(schoolId);
  // Only show loading screen for initial special names load, not background refetches
  const isSpecialNamesInitialLoading = isSpecialNamesLoading && !isSpecialNamesFetching;
  
  // Use mutation hooks for create/update operations
  const createMenuMutation = useCreateMenu();
  const updateMenuMutation = useUpdateMenu();
  const createSpecialNameMutation = useCreateSpecialName();
  const createDishMutation = useCreateDish();
  const deleteMenuItemMutation = useDeleteMenuItem();
  const setMenuItemDishMutation = useSetMenuItemDish();
  const clearMenuItemDishMutation = useClearMenuItemDish();
  
  // Set state from React Query data
  const [dbCourses, setDbCourses] = useState([]);
  const [menuTypeCourses, setMenuTypeCourses] = useState([]);
  
  // Add this new state for tracking dropdown positions
  const [dropdownPositions, setDropdownPositions] = useState({});
  
  const autoResizeTextarea = useCallback((element) => {
    if (element) {
      element.style.height = '1px'; // Collapse to ensure scrollHeight is accurate
      element.style.height = `${element.scrollHeight}px`;
    }
  }, []);

  // Add a new function to prevent browser auto-scrolling when focusing elements
  const preventAutoScroll = useCallback((fn) => {
    // Store current scroll position
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    // Execute the function that might cause scrolling
    fn();
    
    // Restore scroll position
    window.scrollTo(scrollX, scrollY);
  }, []);

  // Add a single useEffect for auto-resizing all textareas
  useEffect(() => {
    if (loading) return; // Don't run during initial load
    
    // Store current scroll position before height adjustments
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    // First, build a mapping of dayNumber -> weekIndex that matches the actual calendar display
    const dayToWeekMap = {};
    
    // Use the same calendar logic as the UI display
    const [year, month] = formData.month.split('-').map(num => parseInt(num, 10));
    const firstDay = new Date(year, month - 1, 1).getDay(); // 0 = Sunday, 1 = Monday, etc.
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1; // Adjust to make Monday = 0
    
    let weekIndex = 0;
    let dayIndexInWeek = 0;
    
    // Map each day to its visual week in the calendar
    formData.days.forEach(day => {
      const dayIndex = (adjustedFirstDay + day.dayNumber - 1) % 7;
      
      // Start a new week when we reach Monday (dayIndex = 0) except for the first day
      if (dayIndex === 0 && day.dayNumber > 1) {
        weekIndex++;
      }
      
      // Only include days that appear in the UI (Monday-Friday, days 0-4 in our week)
      if (dayIndex < 5) { // Only include Monday-Friday
        dayToWeekMap[day.dayNumber] = weekIndex;
      }
    });
    
    // Handle dish textareas - grouped by course (row) AND visual calendar week
    const courseRowsByWeek = {}; // weekIndex-courseId => textareas
    
    // Group textareas by course ID and actual calendar week
    formData.days.forEach(day => {
      if (!day.items || !Array.isArray(day.items)) return;
      
      // Get the week this day belongs to based on the calendar display
      const weekIndex = dayToWeekMap[day.dayNumber];
      if (weekIndex === undefined) return; // Skip days not in the visual calendar
      
      day.items.forEach(item => {
        const courseId = item.courseId;
        const weekCourseKey = `${weekIndex}-${courseId}`; // Composite key combining week and course
        
        if (!courseRowsByWeek[weekCourseKey]) {
          courseRowsByWeek[weekCourseKey] = [];
        }
        
        const key = `${day.dayNumber}-${courseId}`;
        const textareaElement = dishInputRefs.current[key];
        if (textareaElement && item.hasOwnProperty('dishName')) {
          // Reset height to get natural scrollHeight
          textareaElement.style.height = '1px';
          courseRowsByWeek[weekCourseKey].push({
            element: textareaElement,
            scrollHeight: textareaElement.scrollHeight
          });
        }
      });
    });
    
    // For each course row within each week, apply consistent height
    Object.keys(courseRowsByWeek).forEach(weekCourseKey => {
      const rowTextareas = courseRowsByWeek[weekCourseKey];
      if (rowTextareas.length === 0) return;
      
      // Find maximum height needed for this course row within this week
      let maxHeight = 0;
      rowTextareas.forEach(item => {
        maxHeight = Math.max(maxHeight, item.scrollHeight);
      });
      
      // Apply consistent height
      rowTextareas.forEach(item => {
        item.element.style.height = `${maxHeight}px`;
      });
    });
    
    // Handle special name textareas - grouped by visual calendar week
    const weeklySpecialNames = {};
    
    formData.days.forEach(day => {
      // Get the week this day belongs to based on the calendar display
      const weekIndex = dayToWeekMap[day.dayNumber];
      if (weekIndex === undefined) return; // Skip days not in the visual calendar
      
      if (!weeklySpecialNames[weekIndex]) {
        weeklySpecialNames[weekIndex] = [];
      }
      
      const key = `day-${day.dayNumber}`;
      const textareaElement = specialNameInputRefs.current[key];
      
      if (textareaElement && day.hasOwnProperty('specialName')) {
        // Reset height to get natural scrollHeight
        textareaElement.style.height = '1px';
        weeklySpecialNames[weekIndex].push({
          element: textareaElement,
          scrollHeight: textareaElement.scrollHeight
        });
      }
    });
    
    // For each week, apply consistent height to special name textareas
    Object.keys(weeklySpecialNames).forEach(weekIndex => {
      const weekTextareas = weeklySpecialNames[weekIndex];
      if (weekTextareas.length === 0) return;
      
      // Find maximum height needed
      let maxHeight = 0;
      weekTextareas.forEach(item => {
        maxHeight = Math.max(maxHeight, item.scrollHeight);
      });
      
      // Apply consistent height
      weekTextareas.forEach(item => {
        item.element.style.height = `${maxHeight}px`;
      });
    });
    
    // Restore scroll position after height adjustments to prevent jarring jumps
    window.scrollTo(scrollX, scrollY);
  }, [formData.days, formData.month, loading]);
  
  // Add this new function to calculate dropdown positions
  const calculateDropdownPosition = useCallback((key, inputElement) => {
    if (!inputElement) return 'bottom';
    
    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const bottomSpace = viewportHeight - rect.bottom;
    const MIN_SPACE_NEEDED = 200; // Minimum space needed for dropdown to display comfortably
    
    // If there's not enough space below, position it above
    const position = bottomSpace < MIN_SPACE_NEEDED ? 'top' : 'bottom';
    
    setDropdownPositions(prev => ({
      ...prev,
      [key]: position
    }));
    
    return position;
  }, []);
  
  // Update the showDishAutocomplete function
  const showDishAutocomplete = useCallback((key, show) => {
    setShowAutocomplete(prev => ({
      ...prev,
      [key]: show
    }));
    
    // Reset the focused index when opening a dropdown
    if (show) {
      setFocusedDishIndex(prev => ({
        ...prev,
        [key]: -1
      }));
      
      // Calculate dropdown position when showing
      const inputEl = dishInputRefs.current[key];
      calculateDropdownPosition(key, inputEl);
    }
  }, [calculateDropdownPosition]);
  
  // Update the showSpecialNameDropdown function 
  const showSpecialNameDropdown = useCallback((key, show) => {
    setShowSpecialNameAutocomplete(prev => ({
      ...prev,
      [key]: show
    }));
    
    // Reset the focused index when opening a special name dropdown
    if (show) {
      setFocusedSpecialNameIndex(prev => ({
        ...prev,
        [key]: -1
      }));
      
      // Calculate dropdown position when showing
      const inputEl = specialNameInputRefs.current[key];
      calculateDropdownPosition(key, inputEl);
    }
  }, [calculateDropdownPosition]);

  // Create a calendar structure from days
  const calendar = useMemo(() => {
    console.log(`Calculating calendar with ${formData.days.length} days`);
    
    if (!formData.days.length) {
      console.log('No days in formData, returning empty calendar');
      return [];
    }
    
    const [year, month] = formData.month.split('-').map(num => parseInt(num, 10));
    const firstDay = new Date(year, month - 1, 1).getDay(); // 0 = Sunday, 1 = Monday, etc.
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1; // Adjust to make Monday = 0
    
    console.log(`Calendar for ${year}-${month} with ${formData.days.length} days, first day adjusted index: ${adjustedFirstDay}`);
    
    const weeks = [];
    let currentWeek = Array(7).fill(null);
    
    // Fill in days from previous month if needed
    for (let i = 0; i < adjustedFirstDay; i++) {
      currentWeek[i] = null;
    }
    
    // Fill in days of the current month
    formData.days.forEach(day => {
      const dayIndex = (adjustedFirstDay + day.dayNumber - 1) % 7;
      
      if (dayIndex === 0 && day.dayNumber > 1) {
        // Start a new week
        weeks.push([...currentWeek]);
        currentWeek = Array(7).fill(null);
      }
      
      currentWeek[dayIndex] = day;
    });
    
    // Add the last week
    if (currentWeek.some(day => day !== null)) {
      weeks.push([...currentWeek]);
    }
    
    console.log(`Generated ${weeks.length} weeks in the calendar`);
    return weeks;
  }, [formData.days, formData.month]);
  
  // Courses configuration - memoized to prevent recreation on every render
  const courses = useMemo(() => {
    // Log when the courses memo is recalculated
    console.log(`Recalculating courses memoization with language: ${i18n.language}`);
    console.log(`Menu type courses count: ${menuTypeCourses.length}, DB courses count: ${dbCourses.length}`);
    
    // First check if we have menu type-specific courses
    if (menuTypeCourses.length > 0) {
      console.log('Using menu type-specific courses:', menuTypeCourses);
      return menuTypeCourses.map(course => ({
        id: course.id,
        name: course.name,
        display_order: course.display_order
      }));
    }
    
    // If no menu type courses are available, return an empty array
    console.log('No courses available for this menu type, returning empty array');
    return [];
  }, [dbCourses, menuTypeCourses, i18n.language]);

  // Helper function to create empty days array for a month
  const createEmptyDaysArray = useCallback((monthStr, menuTypeId) => {
    console.log(`Creating empty days array for month ${monthStr}, menuTypeId: ${menuTypeId}`);
    console.log(`Using ${courses.length} courses`);
    
    const [year, month] = monthStr.split('-').map(num => parseInt(num, 10));
    const daysInMonth = new Date(year, month, 0).getDate();
    console.log(`Creating ${daysInMonth} days for month: ${year}-${month}`);
    
    return Array.from({ length: daysInMonth }, (_, i) => ({
      dayNumber: i + 1,
      specialName: '',
      specialNameId: null,
      // Only add course items if courses are available
      items: courses.length > 0 ? courses.map(course => ({
        id: null, // Include null ID for new items
        courseId: course.id,
        dishId: null,
        dishName: ''
      })) : []
    }));
  }, [courses]);

  // Update states when React Query data changes
  useEffect(() => {
    console.log('[MenuTypeDebug] menuTypesData updated:', menuTypesData);
    
    if (menuTypesData) {
      // If we have data, use it
      if (menuTypesData.length > 0) {
        console.log('[MenuTypeDebug] Setting menuTypes state with data, count:', menuTypesData.length);
        setMenuTypes(menuTypesData);
      } else {
        // If the data array is empty, check if we already have menu types
        console.log('[MenuTypeDebug] menuTypesData is empty, current menuTypes:', menuTypes);
        // Add a condition to prevent setting the state if we already have a fallback menu type
        if (menuTypes.length <= 1 && !menuTypes.some(type => type.id === 1)) {
          console.log('[MenuTypeDebug] Using fallback menu type');
          setMenuTypes([{ id: 1, name: 'Standard', translated_name: 'Standard' }]);
        }
      }
    } else {
      // If menuTypesData is null/undefined, check loading state
      console.log('[MenuTypeDebug] No menuTypesData yet, isMenuTypesLoading:', isMenuTypesLoading);
      // Add a condition to prevent setting the state if we already have a fallback menu type
      if (!isMenuTypesLoading && menuTypes.length <= 1 && !menuTypes.some(type => type.id === 1)) {
        // If we're not loading but still don't have data, use fallback
        console.log('[MenuTypeDebug] Loading complete but no data, using fallback menu type');
        setMenuTypes([{ id: 1, name: 'Standard', translated_name: 'Standard' }]);
      }
    }
  }, [menuTypesData, isMenuTypesLoading, menuTypes]);
  
  useEffect(() => {
    if (dishesData) {
      setDishes(dishesData);
    }
  }, [dishesData]);
  
  useEffect(() => {
    if (coursesData) {
      setDbCourses(coursesData);
    }
  }, [coursesData]);
  
  useEffect(() => {
    if (menuTypeCoursesData) {
      console.log(`[MenuTypeDebug] menuTypeCoursesData updated for menuTypeId=${formData.menuTypeId}, courses count: ${menuTypeCoursesData.length}`);
      setMenuTypeCourses(menuTypeCoursesData);
      
      // Update lastFetchedMenuTypeIdRef
      lastFetchedMenuTypeIdRef.current = formData.menuTypeId;
      
      // Check if we need to regenerate days based on course structure changes
      const needsRegeneration = !isEditing && (
        formData.days.length === 0 || // No days yet
        formData.days[0]?.items?.length !== menuTypeCoursesData.length || // Different number of courses
        // Check if course IDs don't match
        (formData.days[0]?.items?.length > 0 && 
         !formData.days[0].items.every((item, index) => 
           menuTypeCoursesData[index] && item.courseId === menuTypeCoursesData[index].id
         ))
      );
      
      if (needsRegeneration) {
        console.log('[MenuTypeDebug] Regenerating days with new courses from menu type');
        // Initialize days with the new courses
        const newDays = createEmptyDaysArray(formData.month, formData.menuTypeId);
        setFormData(prev => ({
          ...prev,
          days: newDays
        }));
      } else {
        console.log('[MenuTypeDebug] No need to regenerate days, course structure unchanged');
      }
      
      // If in editing mode, only check for course count mismatch
      if (isEditing) {
        // Check if the items count per day doesn't match the courses count
        const firstDay = formData.days[0];
        if (firstDay && firstDay.items && firstDay.items.length !== menuTypeCoursesData.length) {
          console.log(`[MenuTypeDebug] Course count mismatch detected! Day has ${firstDay.items.length} items but menu type has ${menuTypeCoursesData.length} courses`);
          console.log('[MenuTypeDebug] Current days structure:', formData.days);
          console.log('[MenuTypeDebug] New menu type courses:', menuTypeCoursesData);
        }
      }
    } else {
      console.log(`[MenuTypeDebug] menuTypeCoursesData is empty or null for menuTypeId=${formData.menuTypeId}`);
    }
  }, [menuTypeCoursesData, formData.menuTypeId, formData.month, formData.days, isEditing, createEmptyDaysArray]);
  
  // Update specialNames when specialNamesData changes
  useEffect(() => {
    if (specialNamesData) {
      setSpecialNames(specialNamesData);
    }
  }, [specialNamesData]);
  
  // Update loading state based on React Query loading states
  useEffect(() => {
    const isDataLoading = isMenuTypesInitialLoading || 
                          isDishesInitialLoading || 
                          isCoursesInitialLoading || 
                          isMenuTypeCoursesInitialLoading ||
                          isSpecialNamesInitialLoading ||
                          (isEditing && isMenuInitialLoading);
    
    setLoading(isDataLoading);
  }, [isMenuTypesInitialLoading, isDishesInitialLoading, isCoursesInitialLoading, 
      isMenuTypeCoursesInitialLoading, isMenuInitialLoading, isSpecialNamesInitialLoading, isEditing]);
  
  // Load menu data if editing
  useEffect(() => {
    if (!isEditing || !menuId || !menuData) return;
    
    try {
      console.log(`Menu data loaded with language ${language}:`, menuData);
      
      // Create month string in YYYY-MM format
      const monthStr = `${menuData.year}-${menuData.month.toString().padStart(2, '0')}`;
      
      // Get list of all days in the month
      const [year, month] = monthStr.split('-').map(num => parseInt(num, 10));
      const daysInMonth = new Date(year, month, 0).getDate();
      
      // Get courses to use (either menu type specific or all courses)
      const coursesToUse = menuTypeCoursesData && menuTypeCoursesData.length > 0 
        ? menuTypeCoursesData.map(course => ({
            id: course.id,
            name: course.name,
            display_order: course.display_order
          }))
        : courses;
      
      // Create a full array of days with default empty values
      const allDays = Array.from({ length: daysInMonth }, (_, i) => {
        const dayNumber = i + 1;
        // Try to find this day in the loaded data
        const existingDay = menuData.days.find(d => d.day === dayNumber);
        
        if (existingDay) {
          // Map existing day data
          return {
            id: existingDay.id,
            dayNumber,
            specialName: existingDay.specialName || '',
            specialNameId: existingDay.specialNameId || null,
            has_special_name_requested_translation: existingDay.has_special_name_requested_translation, // <-- Add this line
            has_unread_comments: existingDay.has_unread_comments || false,
            items: coursesToUse.map(course => {
              // Find the course in the existing day's items
              const existingItem = existingDay.items.find(i => 
                i.course_id === course.id
              );
              
              return {
                id: existingItem ? existingItem.id : null, // Preserve the menu item ID
                courseId: course.id,
                dishId: existingItem ? existingItem.dish_id : null,
                dishName: existingItem ? existingItem.dish_name : '',
                has_requested_translation: existingItem ? existingItem.has_requested_translation : false // <-- Add this line
              };
            })
          };
        } else {
          // Create an empty day with courses specific to this menu type
          return {
            dayNumber,
            specialName: '',
            specialNameId: null,
            items: coursesToUse.map(course => ({
              id: null, // Include null ID for new items
              courseId: course.id,
              dishId: null,
              dishName: ''
            }))
          };
        }
      });
      
      console.log('Transformed days:', allDays);
      
      // Transform data to match form structure
      const transformedData = {
        month: monthStr,
        menuTypeId: menuData.menu_type_id,
        days: allDays
      };
      
      setFormData(transformedData);
      // Store original form data for change detection
      setOriginalFormData(JSON.stringify(transformedData));
    } catch (err) {
      console.error('Failed to process menu data:', err);
      setError('Failed to process menu data. Please try again later.');
    }
  }, [isEditing, menuId, menuData, language, courses, menuTypeCoursesData]);
  
  // Fetch special names (not converted to React Query yet)
  useEffect(() => {
    const fetchSpecialNames = async () => {
      if (!schoolId) return;
      
      try {
        const specialNamesData = await specialNameService.getSpecialNames(schoolId, language);
        setSpecialNames(specialNamesData);
      } catch (specialNameError) {
        console.error('Failed to fetch special names:', specialNameError);
        setSpecialNames([]);
      }
    };
    
    fetchSpecialNames();
  }, [schoolId, language]);
  
  // Effect to initialize menu type ID from school's default or lowest available
  useEffect(() => {
    // Skip if we're editing an existing menu, if menuTypeId is already set, or if we've applied URL parameters
    if (isEditing || formData.menuTypeId !== null || locationStateAppliedRef.current) return;
    
    const initializeMenuTypeId = () => {
      console.log('Initializing menu type ID from school or lowest available');
      
      // First try to use the school's default menu type ID
      if (school?.default_menu_type_id) {
        console.log(`Using school's default menu type ID: ${school.default_menu_type_id}`);
        setFormData(prev => ({
          ...prev,
          menuTypeId: school.default_menu_type_id
        }));
        return;
      }
      
      // If menuTypes are loaded from React Query
      if (menuTypes && menuTypes.length > 0) {
        // Sort menu types by ID and use the lowest
        const lowestMenuTypeId = [...menuTypes].sort((a, b) => a.id - b.id)[0].id;
        console.log(`No school default menu type, using lowest ID: ${lowestMenuTypeId}`);
        setFormData(prev => ({
          ...prev,
          menuTypeId: lowestMenuTypeId
        }));
      } else {
        // If no menu types available, default to 1 as a last resort
        console.log('No menu types found, defaulting to ID 1');
        setFormData(prev => ({
          ...prev,
          menuTypeId: 1
        }));
      }
    };
    
    if (!isMenuTypesLoading) {
      initializeMenuTypeId();
    }
  }, [isEditing, formData.menuTypeId, locationStateAppliedRef, school, menuTypes, isMenuTypesLoading]);
  
  // Load user data only if contextUser is not available
  useEffect(() => {
    // Skip if contextUser is already available
    if (contextUser) {
      setUser(contextUser);
      return;
    }
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (!userData.current_school?.id) {
        throw new Error('No school ID found');
      }
      setUser(userData);
    } catch (err) {
      setError('Failed to load user data. Please log in again.');
    }
  }, [contextUser]); // Add contextUser as dependency
  
  // Extract query parameters on mount - this must run BEFORE initializing menu type ID
  useEffect(() => {
    console.log(`[Effect QueryParams] Running. isEditing=${isEditing}, locationStateApplied=${locationStateAppliedRef.current}, search=${location.search}`);
    // Skip if we're editing an existing menu
    if (isEditing || locationStateAppliedRef.current) {
      console.log(`[Effect QueryParams] Skipping.`);
      return;
    }
    
    // Get query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const yearParam = urlParams.get('year');
    const monthParam = urlParams.get('month');
    const menuTypeIdParam = urlParams.get('menuTypeId');
    
    // If we have query parameters, use them to set initial form data
    if (yearParam || monthParam || menuTypeIdParam) {
      console.log('[Effect QueryParams] Applying URL parameters:', { 
        year: yearParam, 
        month: monthParam, 
        menuTypeId: menuTypeIdParam 
      });
      
      // Format the month value if both year and month are provided
      const formattedMonth = yearParam && monthParam 
        ? `${yearParam}-${monthParam.padStart(2, '0')}` 
        : formData.month;
      
      setFormData(prev => {
        const newState = {
          ...prev,
          month: formattedMonth,
          menuTypeId: menuTypeIdParam ? parseInt(menuTypeIdParam, 10) : prev.menuTypeId,
        };
        console.log(`[Effect QueryParams] setFormData with month=${newState.month}, menuTypeId=${newState.menuTypeId}`);
        return newState;
      });
      
      // Mark that we've applied URL parameters
      locationStateAppliedRef.current = true;
      console.log(`[Effect QueryParams] Set locationStateAppliedRef.current = true`);
    } else {
      console.log(`[Effect QueryParams] No relevant URL parameters found.`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, location.search]); // Add location.search as dependency to ensure it runs when URL changes
  
  // Apply location state (for backwards compatibility)
  useEffect(() => {
    const locationState = location.state;
    console.log('Location state:', locationState);
    
    // Only apply location state once, and only when not editing
    if (!isEditing && locationState && !locationStateAppliedRef.current) {
      // Create a new stable reference for the form data
      const monthToUse = locationState.month || formData.month;
      const menuTypeToUse = locationState.menuTypeId || formData.menuTypeId;
      const newDays = createEmptyDaysArray(monthToUse, menuTypeToUse);
      
      const newFormData = {
        month: monthToUse,
        menuTypeId: menuTypeToUse,
        days: newDays
      };
      
      // First update the form data
      setFormData(newFormData);
      
      // Then set the original form data
      console.log('Setting original form data from location state');
      setOriginalFormData(JSON.stringify(newFormData));
      
      // Explicitly set loading to false since we have the data from location state
      console.log('Redirected to create new menu with state - setting loading to false');
      setLoading(false);
      
      // Mark that we've applied the location state
      locationStateAppliedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isEditing, createEmptyDaysArray, setFormData, setLoading]);
  
  // Check for existing menu if not in edit mode
  useEffect(() => {
    console.log(`[Effect CheckExisting] Running. isEditing=${isEditing}, menuTypeId=${formData.menuTypeId}`);
    if (isEditing || formData.menuTypeId === null) {
      console.log(`[Effect CheckExisting] Skipping (isEditing or menuTypeId null).`);
      return;
    }
    
    // Check if we should skip the existing menu check (set by handleMonthChange)
    const shouldSkipCheck = sessionStorage.getItem('skipExistingMenuCheck') === 'true';
    if (shouldSkipCheck) {
      console.log('[Effect CheckExisting] Skipping automatic check due to skipExistingMenuCheck flag.');
      sessionStorage.removeItem('skipExistingMenuCheck');
      console.log('[Effect CheckExisting] Removed skipExistingMenuCheck flag.');
      // Ensure loading is set to false if we skipped the check
      setLoading(false); 
      return;
    }
    
    // Get school ID using our helper function
    const schoolId = getSchoolId();
    
    if (!schoolId) {
      console.warn('[Effect CheckExisting] No school ID available, skipping check.');
      return;
    }
    
    const checkExistingMenu = async () => {
      try {
        // setLoading(true); // <-- REMOVE THIS LINE
        console.log('[Effect CheckExisting] Calling checkExistingMenu API.');
        
        const existingMenu = await menuService.checkExistingMenu(
          schoolId,
          formData.month,
          formData.menuTypeId
        );
        
        console.log('[Effect CheckExisting] API response:', existingMenu);
        
        if (existingMenu) {
          console.log(`[Effect CheckExisting] Found existing menu ID: ${existingMenu.id}, navigating...`);
          navigate(getMenuEditorUrl(school?.subdomain, existingMenu.id));
        } else {
          console.log('[Effect CheckExisting] No existing menu found.');
          setLoading(false);
        }
      } catch (err) {
        console.error('[Effect CheckExisting] Error checking for existing menu:', err);
        setLoading(false);
      }
    };
    
    checkExistingMenu();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isEditing, school, formData.menuTypeId, formData.month, getSchoolId]);
  
  // Check for unsaved changes - use a ref to track previous values to avoid unnecessary rerenders
  const previousFormDataRef = useRef('');
  
  // Check for unsaved changes
  useEffect(() => {
    // Skip during loading or if original form data isn't initialized
    if (loading || !originalFormData) {
      console.log('Skipping change detection - loading or no originalFormData');
      return;
    }
    
    // Stringify current form data
    const currentFormData = JSON.stringify(formData);
    
    // Skip if the form data hasn't actually changed from the previous check
    if (currentFormData === previousFormDataRef.current) {
      console.log('Form data unchanged since last check, skipping');
      return;
    }
    
    // Update the ref with current value
    previousFormDataRef.current = currentFormData;
    
    // Use a debounce to avoid excessive change checks during input
    const debounceTimer = setTimeout(() => {
      try {
        console.log('Checking for changes');
        const hasUnsavedChanges = currentFormData !== originalFormData;
        
        // Only update if the value has actually changed
        if (hasChanges !== hasUnsavedChanges) {
          console.log(`Setting hasChanges to ${hasUnsavedChanges}`);
          setHasChanges(hasUnsavedChanges);
          
          // Dispatch event for Navbar to detect changes
          const event = new CustomEvent('formChanged', { 
            detail: { hasChanges: hasUnsavedChanges }
          });
          window.dispatchEvent(event);
        }
      } catch (err) {
        console.error('Error in change detection:', err);
      }
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [formData, originalFormData, loading, hasChanges, setHasChanges]);
  
  // Handle loading state for new menu creation
  useEffect(() => {
    if (!isEditing && user) {
      console.log('Creating new menu - setting loading to false');
      const timer = setTimeout(() => {
        setLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isEditing, user]);
  
  // Helper function to close all autocompletes except the one specified - memoized to prevent unnecessary re-renders
  const closeOtherAutocompletes = useCallback((exceptKey = null, type = 'dish') => {
    // Always close all dish autocompletes when type is specialName
    if (type === 'specialName') {
      setShowAutocomplete({});
    }
    
    // Always close all special name autocompletes when type is dish
    if (type === 'dish') {
      setShowSpecialNameAutocomplete({});
    }
    
    // Now handle the specific type being opened
    if (type === 'dish') {
      setShowAutocomplete(prev => {
        const result = {};
        if (exceptKey) {
          result[exceptKey] = true;
        }
        return result;
      });
    } else if (type === 'specialName') {
      setShowSpecialNameAutocomplete(prev => {
        const result = {};
        if (exceptKey) {
          result[exceptKey] = true;
        }
        return result;
      });
    }
  }, []);
  
  // Handle form value changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    console.log(`[HandleChange] Field changed: ${name} = ${value}, isEditing: ${isEditing}`);
    console.log(`[MenuTypeDebug] Current state before change - menuTypeId: ${formData.menuTypeId}, month: ${formData.month}`);
    
    // Special case for month selection in edit mode to prevent auto-redirection
    if (name === 'month' && isEditing) {
      handleMonthChange(value);
      return;
    }
    
    // Special case for menu type selection in edit mode
    if (name === 'menuTypeId' && isEditing) {
      handleMenuTypeChange(parseInt(value, 10));
      return;
    }
    
    // For create mode, handle form updates carefully to avoid remounting
    if (!isEditing) {
      setFormData(prev => {
        // For month changes, need to regenerate the days
        if (name === 'month') {
          console.log(`[MenuTypeDebug] Regenerating days for new month: ${value}`);
          return {
            ...prev,
            [name]: value,
            days: createEmptyDaysArray(value, prev.menuTypeId)
          };
        }
        
        // For menu type changes, update the menuTypeId but don't regenerate days yet
        if (name === 'menuTypeId') {
          const newMenuTypeId = parseInt(value);
          console.log(`[MenuTypeDebug] Updating menuTypeId in create mode: ${prev.menuTypeId} -> ${newMenuTypeId}`);
          console.log(`[MenuTypeDebug] Value type: ${typeof value}, parsed type: ${typeof newMenuTypeId}`);
          
          // Log the available menu types for debugging
          console.log(`[MenuTypeDebug] Available menu types:`, menuTypes.map(t => ({ id: t.id, name: t.name })));
          console.log(`[MenuTypeDebug] Selected menu type:`, menuTypes.find(mt => mt.id === newMenuTypeId));
          
          // First just update the menuTypeId
          const updatedState = {
            ...prev,
            [name]: newMenuTypeId
          };
          
          // After menuTypeId is updated, menuTypeCoursesData will be refetched.
          // We're going to immediately regenerate the days based on the new menu type
          // to ensure the correct course count is used.
          console.log(`[MenuTypeDebug] Regenerating days with new menu type courses`);
          
          // Get the expected new courses that will be available
          const expectedMenuType = menuTypes.find(mt => mt.id === newMenuTypeId);
          console.log(`[MenuTypeDebug] Selected menu type details:`, expectedMenuType);
          
          // Schedule a regeneration of days with the new courses
          // This will run after the component updates with the new menuTypeId
          setTimeout(() => {
            // By this time, menuTypeCoursesData should have been updated
            console.log(`[MenuTypeDebug] Delayed regeneration of days for menu type ${newMenuTypeId}`);
            console.log(`[MenuTypeDebug] Current menu type courses:`, menuTypeCourses);
            
            // Use createEmptyDaysArray which will use the updated courses
            const newDays = createEmptyDaysArray(prev.month, newMenuTypeId);
            console.log(`[MenuTypeDebug] Generated ${newDays.length} new days with courses:`, 
              newDays.length > 0 ? newDays[0].items.map(i => i.courseId) : []);
            
            setFormData(current => ({
              ...current,
              days: newDays
            }));
          }, 100);
          
          return updatedState;
        }
        
        // For other changes, just update the value
        return {
          ...prev,
          [name]: value
        };
      });
    } else {
      // For edit mode, we can use the simpler update logic
      if (name === 'menuTypeId') {
        // This is now handled by handleMenuTypeChange, so this code won't be reached
        console.log(`[HandleChange] menuTypeId changes in edit mode are now handled by handleMenuTypeChange`);
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Check for existing menu if month or menuTypeId changes in create mode
    if ((name === 'month' || name === 'menuTypeId') && !isEditing && getSchoolId()) {
      // Check if we should skip the existing menu check
      const shouldSkipCheck = sessionStorage.getItem('skipExistingMenuCheck') === 'true';
      if (shouldSkipCheck) {
        console.log('[HandleChange] Skipping existing menu check due to skipExistingMenuCheck flag');
        sessionStorage.removeItem('skipExistingMenuCheck');
        console.log('[HandleChange] Removed skipExistingMenuCheck flag.');
        setLoading(false);
        return;
      }
      
      const monthToCheck = name === 'month' ? value : formData.month;
      let menuTypeToCheck;
      if (name === 'menuTypeId') {
        menuTypeToCheck = parseInt(value, 10);
      } else {
        menuTypeToCheck = typeof formData.menuTypeId === 'string' 
          ? parseInt(formData.menuTypeId, 10) 
          : formData.menuTypeId;
      }
      
      console.log(`[HandleChange] Initiating existing menu check for month: ${monthToCheck}, menuType: ${menuTypeToCheck}`);
      
      // Check for an existing menu
      (async () => {
        try {
          // setLoading(true); // <-- REMOVE THIS LINE
          const schoolId = getSchoolId();
          const existingMenu = await menuService.checkExistingMenu(
            schoolId,
            monthToCheck,
            menuTypeToCheck
          );
          
          console.log(`[HandleChange] checkExistingMenu response:`, existingMenu);
          
          if (existingMenu) {
            console.log(`[HandleChange] Found existing menu ID: ${existingMenu.id}`);
            const existingId = parseInt(existingMenu.id);
            const currentId = parseInt(menuId);
            
            if (!isEditing || (isEditing && existingId !== currentId)) {
              console.log(`[HandleChange] Redirecting from ${currentId || 'new'} to edit existing: ${existingId}`);
              const targetUrl = getMenuEditorUrl(school?.subdomain, existingId);
              console.log(`[HandleChange] Navigate target URL: ${targetUrl}`);
              
              // When navigating to a different menu ID, reset location state flag
              if (isEditing && existingId !== currentId) {
                locationStateAppliedRef.current = false;
                console.log('[HandleChange] Reset locationStateAppliedRef for menu ID change');
              }
              
              navigate(targetUrl);
              return; 
            } else {
              console.log('[HandleChange] Found current menu, not redirecting');
            }
          } else {
            console.log('[HandleChange] No existing menu found.');
            if (isEditing) {
              console.log('[HandleChange] Redirecting from edit mode to create mode.');
              sessionStorage.setItem('clearMenuFormData', 'true');
              console.log('[HandleChange] Set clearMenuFormData=true');
              
              const queryParams = { month: monthToCheck, menuTypeId: menuTypeToCheck };
              const targetUrl = getMenuEditorUrl(school?.subdomain, null, queryParams);
              console.log(`[HandleChange] Navigate target URL (create): ${targetUrl}`);
              
              // Set flag to skip check on next load
              sessionStorage.setItem('skipExistingMenuCheck', 'true');
              console.log('[HandleChange] Setting skipExistingMenuCheck flag.');

              // Reset locationStateAppliedRef to ensure URL parameters are processed in the next render
              locationStateAppliedRef.current = false;
              console.log('[HandleChange] Reset locationStateAppliedRef to false');

              navigate(targetUrl);
              return;
            }
            setLoading(false);
          }
        } catch (err) {
          console.error('[HandleChange] Error checking for existing menu:', err);
          setLoading(false);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, user, formData.month, menuId, navigate, createEmptyDaysArray, formData.menuTypeId, school, menuTypes]);
  
  // Track unsaved changes and prompt before leaving
  useEffect(() => {
    // ... existing code ...
  }, [isEditing, user, formData.month, menuId, navigate, createEmptyDaysArray, formData.menuTypeId]);
  
  // Handle day special name changes and autocomplete
  const handleDayChange = useCallback((dayNumber, value) => {
    console.log(`Updating special name: day ${dayNumber}, value: ${value}`);
    
    // Update the special name in the form
    setFormData(prev => {
      // Find the specific day we're modifying
      const targetDay = prev.days.find(day => day.dayNumber === dayNumber);
      
      // If the value hasn't actually changed, return the same state to prevent re-render
      if (targetDay && targetDay.specialName === value) {
        console.log('Special name value unchanged, skipping update');
        return prev;
      }
      
      const newDays = prev.days.map(day => {
        if (day.dayNumber === dayNumber) {
          return {
            ...day,
            specialName: value,
            // Clear specialNameId when text changes directly
            // This is important with the new architecture where special names are dishes
            specialNameId: null
          };
        }
        return day;
      });
      
      return {
        ...prev,
        days: newDays
      };
    });
    
    // Show autocomplete if value is not empty
    const key = `day-${dayNumber}`;
    
    if (value.length > 0) {
      // Close all other autocompletes when opening this one
      closeOtherAutocompletes(key, 'specialName');
      setShowAutocomplete({}); // Close dish autocompletes
      
      // Filter special names for autocomplete
      const filtered = specialNames.filter(specialName => 
        specialName.name && containsIgnoringAccents(specialName.name, value)
      );
      setSpecialNameAutocompleteResults(prev => ({
        ...prev,
        [key]: filtered
      }));
      
      // Show autocomplete and reset focused index
      showSpecialNameDropdown(key, true);
    } else {
      showSpecialNameDropdown(key, false);
    }
  }, [closeOtherAutocompletes, specialNames, showSpecialNameDropdown]);
  
  // Handle selection from special name autocomplete
  const handleSpecialNameAutocompleteSelect = (dayNumber, specialName) => {
    setFormData(prev => {
      const newDays = prev.days.map(day => {
        if (day.dayNumber === dayNumber) {
          return {
            ...day,
            specialName: specialName.name,
            specialNameId: specialName.id,
            has_special_name_requested_translation: true // Ensure flag is set on select
          };
        }
        return day;
      });
      
      return {
        ...prev,
        days: newDays
      };
    });
    
    // Hide all autocompletes
    setShowAutocomplete({});
    setShowSpecialNameAutocomplete({});
  };
  
  // Handle creating a new special name from input
  const handleCreateNewSpecialName = async (dayNumber, specialNameText) => {
    if (!specialNameText) return null;
    
    try {
      const schoolId = getSchoolId();
      if (!schoolId) {
        console.error('No school ID available');
        return null;
      }
      
      console.log(`Creating special name: ${specialNameText} for school ${schoolId}`);
      
      // Create data for new special name with proper structure for the API
      const newSpecialNameData = {
        schoolId,
        isSpecialName: true,
        translations: [
          {
            language_code: i18n.language,
            name: specialNameText.trim()
          }
        ]
      };
      
      // Use the mutation hook to create the special name
      const newSpecialName = await createSpecialNameMutation.mutateAsync(newSpecialNameData);
      
      console.log('Created new special name:', newSpecialName);
      
      // Add the new special name to the local state
      setSpecialNames(prev => [...prev, newSpecialName]);

      // Select the new special name for the day
      setFormData(prev => {
        const newDays = prev.days.map(day => {
          if (day.dayNumber === dayNumber) {
            return {
              ...day,
              specialName: specialNameText.trim(),
              specialNameId: newSpecialName.id,
              has_special_name_requested_translation: true // Optimistically set flag
            };
          }
          return day;
        });
        
        return {
          ...prev,
          days: newDays
        };
      });
      
      // Close all autocompletes
      setShowAutocomplete({});
      setShowSpecialNameAutocomplete({});
      
      return newSpecialName;
    } catch (error) {
      console.error('Failed to create special name:', error);
      setError(`Failed to create special name: ${error.message}`);
      return null;
    }
  };
  
  // Handle dish input changes and autocomplete
  const handleDishInputChange = useCallback((dayNumber, courseId, value) => {
    console.log(`Updating dish input: day ${dayNumber}, course ${courseId}, value: ${value}`);
    
    // Update the dish name in the form
    setFormData(prev => {
      // Find the specific day and item we're modifying
      const targetDay = prev.days.find(day => day.dayNumber === dayNumber);
      const targetItem = targetDay?.items.find(item => item.courseId === courseId);
      
      // If the value hasn't actually changed, return the same state to prevent re-render
      if (targetItem && targetItem.dishName === value) {
        console.log('Dish name value unchanged, skipping update');
        return prev;
      }
      
      const newDays = prev.days.map(day => {
        if (day.dayNumber === dayNumber) {
          const newItems = day.items.map(item => {
            if (item.courseId === courseId) {
              return {
                ...item,
                dishName: value,
                dishId: null // Clear dish ID when text changes
              };
            }
            return item;
          });
          
          return {
            ...day,
            items: newItems
          };
        }
        return day;
      });
      
      return {
        ...prev,
        days: newDays
      };
    });
    
    // Show autocomplete if value is not empty
    const key = `${dayNumber}-${courseId}`;
    
    if (value.length > 0) {
      // Close all other autocompletes when opening this one
      closeOtherAutocompletes(key, 'dish');
      setShowSpecialNameAutocomplete({}); // Close special name autocompletes
      
      // Filter dishes for autocomplete
      const filtered = dishes.filter(dish => 
        dish.name && containsIgnoringAccents(dish.name, value)
      );
      setAutocompleteResults(prev => ({
        ...prev,
        [key]: filtered
      }));
      
      // Show autocomplete and reset focused index
      showDishAutocomplete(key, true);
    } else {
      showDishAutocomplete(key, false);
    }
  }, [closeOtherAutocompletes, dishes, showDishAutocomplete]);
  
  // Handle selection from autocomplete
  const handleAutocompleteSelect = (dayNumber, courseId, dish) => {
    console.log(`Selected dish for day ${dayNumber}, course ${courseId}:`, dish);
    
    const day = formData.days.find(d => d.dayNumber === dayNumber);
    if (!day) return;
    
    const item = day.items.find(i => i.courseId === courseId);
    if (!item) return;

    // If this is a published menu with IDs, use the optimistic update
    if (menuId && day.id && item.id) {
      // Only call the API if we have a valid item ID
      console.log('Making API call to update menu item dish with item ID:', item.id);
      
      // Use the mutation hook for optimistic updates
      setMenuItemDishMutation.mutateAsync({
        menuId,
        dayId: day.id,
        itemId: item.id,
        dishId: dish.id,
        dishName: dish.name
      }).catch(err => {
        console.error('Failed to update menu item dish:', err);
        setError('Failed to set dish. Please try again.');
      });
    } else {
      // For new menus or new items without IDs, we'll save the dish when the whole form is submitted
      console.log('Item does not have an ID yet, will save when the form is submitted');
    }
    
    // Update form data in addition to the optimistic update
    setFormData(prev => {
      const newDays = prev.days.map(d => {
        if (d.dayNumber === dayNumber) {
          const newItems = d.items.map(i => {
            if (i.courseId === courseId) {
              return {
                ...i,
                dishId: dish.id,
                dishName: dish.name,
                has_requested_translation: true // Ensure flag is set on select
              };
            }
            return i;
          });
          
          return {
            ...d,
            items: newItems
          };
        }
        return d;
      });
      
      return {
        ...prev,
        days: newDays
      };
    });
    
    // Hide all autocompletes
    setShowAutocomplete({});
    setShowSpecialNameAutocomplete({});
  };
  
  // Handle creating a new dish from input
  const handleCreateNewDish = async (dayNumber, courseId, dishName) => {
    if (!dishName.trim()) return null;
    
    try {
      const schoolId = getSchoolId();
      if (!schoolId) {
        console.error('No school ID available');
        return null;
      }
      
      // First, check if this dish name already exists in the current language
      const matchingDish = dishes.find(dish => 
        dish.name && dish.name.toLowerCase() === dishName.trim().toLowerCase()
      );
      
      // If the dish already exists, select it instead of creating a new one
      if (matchingDish) {
        console.log(`Found existing dish with name "${dishName.trim()}", using ID: ${matchingDish.id}`);
        
        // Find the day and item to update
        const day = formData.days.find(d => d.dayNumber === dayNumber);
        if (day && day.id) {
          // If this is an existing menu with IDs, use the optimistic update
          const item = day.items.find(i => i.courseId === courseId);
          if (item && item.id) {
            // Use the mutation hook for optimistic updates
            setMenuItemDishMutation.mutateAsync({
              menuId,
              dayId: day.id,
              itemId: item.id,
              dishId: matchingDish.id,
              dishName: matchingDish.name
            }).catch(err => {
              console.error('Failed to update menu item dish:', err);
              setError('Failed to set dish. Please try again.');
            });
          }
        }
        
        // Select the existing dish
        setFormData(prev => {
          const newDays = prev.days.map(day => {
            if (day.dayNumber === dayNumber) {
              const newItems = day.items.map(item => {
                if (item.courseId === courseId) {
                  return {
                    ...item,
                    dishId: matchingDish.id,
                    dishName: matchingDish.name
                  };
                }
                return item;
              });
              
              return {
                ...day,
                items: newItems
              };
            }
            return day;
          });
          
          return {
            ...prev,
            days: newDays
          };
        });
        
        // Close all autocompletes
        setShowAutocomplete({});
        setShowSpecialNameAutocomplete({});
        
        // Return the matched dish
        return matchingDish;
      }
      
      // Create data for new dish with proper structure for the API
      const dishData = {
        schoolId: schoolId,
        translations: [
          {
            language_code: i18n.language,
            name: dishName.trim()
          }
        ]
      };
      
      // Use the mutation hook to create the dish
      const newDish = await createDishMutation.mutateAsync(dishData);
      console.log('Created new dish:', newDish);
      
      // Add the dish to the list (although React Query should handle this via cache)
      setDishes(prev => [...prev, { 
        id: newDish.id, 
        name: dishName.trim() 
      }]);
      
      // Find the day and item to update
      const day = formData.days.find(d => d.dayNumber === dayNumber);
      if (day && day.id && menuId) {
        // If this is an existing menu with IDs, use the optimistic update
        const item = day.items.find(i => i.courseId === courseId);
        if (item && item.id) {
          // Only call the API if we have a valid item ID
          console.log('Making API call to update menu item dish with item ID:', item.id);
          
          // Use the mutation hook for optimistic updates
          setMenuItemDishMutation.mutateAsync({
            menuId,
            dayId: day.id,
            itemId: item.id,
            dishId: newDish.id,
            dishName: dishName.trim()
          }).catch(err => {
            console.error('Failed to update menu item dish:', err);
            setError('Failed to set dish. Please try again.');
          });
        } else {
          // For a newly added item without an ID, we'll save it when the form is submitted
          console.log('New menu item without ID, will save when form is submitted');
        }
      }
      
      // Select the new dish
      setFormData(prev => {
        const newDays = prev.days.map(day => {
          if (day.dayNumber === dayNumber) {
            const newItems = day.items.map(item => {
              if (item.courseId === courseId) {
                return {
                  ...item,
                  dishId: newDish.id,
                  dishName: dishName.trim(),
                  has_requested_translation: true // Optimistically set flag
                };
              }
              return item;
            });
            
            return {
              ...day,
              items: newItems
            };
          }
          return day;
        });
        
        return {
          ...prev,
          days: newDays
        };
      });
      
      // Close all autocompletes
      setShowAutocomplete({});
      setShowSpecialNameAutocomplete({});
      
      // Return the newly created dish for tracking
      return newDish;
      
    } catch (error) {
      console.error('Failed to create dish:', error);
      setError(`Failed to create dish: ${error.message}`);
      return null;
    }
  };
  
  // Helper function to get language id from language code
  // eslint-disable-next-line no-unused-vars
  const languageCodeMap = {
    en: 1,
    es: 2,
    ca: 3
  };
  
  // eslint-disable-next-line no-unused-vars
  const getLanguageId = (code) => {
    return languageCodeMap[code] || 1; // Default to English (1)
  };
  
  // Add a state to track if the window is visible
  const [isWindowVisible, setIsWindowVisible] = useState(true);
  
  // Add an effect to track document visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsWindowVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Handle focus on special name input
  const handleSpecialNameFocus = (dayNumber) => {
    // Prevent auto-scrolling
    preventAutoScroll(() => {
      // Generate a unique key for this day
      const key = `day-${dayNumber}`;
      
      // Close ALL other autocompletes first
      setShowAutocomplete({}); // Close all dish autocompletes
      setShowSpecialNameAutocomplete(prev => {
        // Reset to empty object first
        const result = {};
        // Only keep the current key
        result[key] = true;
        return result;
      });
      
      // Show special name autocomplete
      showSpecialNameDropdown(key, true);
      
      // Get the current value in the input field to filter results
      const day = formData.days.find(d => d.dayNumber === dayNumber);
      const currentValue = day?.specialName || '';
      
      // Populate with filtered special names based on current input value
      if (specialNames && specialNames.length > 0) {
        const filtered = specialNames.filter(name => 
          name && typeof name.name === 'string' && 
          (currentValue === '' || containsIgnoringAccents(name.name, currentValue))
        ).sort((a, b) => a.name.localeCompare(b.name));
        
        setSpecialNameAutocompleteResults(prev => ({
          ...prev,
          [key]: filtered
        }));
        
        // Log dropdown content
        if (filtered.length > 0) {
          console.log(`Showing ${filtered.length} special names for day ${dayNumber}`);
        } else {
          console.log(`No special names found for dropdown day ${dayNumber}`);
        }
      } else {
        console.log('No special names available for dropdown');
      }
    });
  };
  
  // Handle focus on dish input
  const handleDishFocus = (dayNumber, courseId) => {
    // Prevent auto-scrolling
    preventAutoScroll(() => {
      // Trigger dishes loading when a dish input gets focus - progressive loading
      setDishesLoaded(true);
      
      // Generate a unique key for this day and course
      const key = `${dayNumber}-${courseId}`;
      
      // Close ALL other autocompletes first
      setShowSpecialNameAutocomplete({}); // Close all special name autocompletes
      setShowAutocomplete(prev => {
        // Reset to empty object first
        const result = {};
        // Only keep the current key
        result[key] = true;
        return result;
      });
      
      // Show autocomplete dropdown
      showDishAutocomplete(key, true);
      
      // Get the current value in the input field to filter results
      const day = formData.days.find(d => d.dayNumber === dayNumber);
      const item = day?.items?.find(i => i.courseId === courseId);
      const currentValue = item?.dishName || '';
      
      // Get filtered dishes for dropdown based on current input value
      if (dishes && dishes.length > 0) {
        const filtered = dishes.filter(dish => 
          dish && typeof dish.name === 'string' && 
          (currentValue === '' || containsIgnoringAccents(dish.name, currentValue))
        ).sort((a, b) => {
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        });
        
        setAutocompleteResults(prev => ({
          ...prev,
          [key]: filtered
        }));
        
        // Log dropdown content
        if (filtered.length > 0) {
          console.log(`Showing ${filtered.length} dishes for day ${dayNumber}, course ${courseId}`);
        } else {
          console.log(`No dishes found for dropdown day ${dayNumber}, course ${courseId}`);
        }
      } else {
        console.log('No dishes available for dropdown');
      }
    });
  };
  
  // Helper function to find a translation in any language for a dish
  const findAvailableTranslation = useCallback((translations) => {
    if (!translations || translations.length === 0) return null;
    
    // First try to find a translation in the current language
    const currentLangTranslation = translations.find(t => 
      t.language_code === i18n.language && t.name
    );
    
    if (currentLangTranslation) return {
      name: currentLangTranslation.name,
      language: i18n.language,
      isCurrentLanguage: true
    };
    
    // Next, try to find a translation in the school's default language
    if (defaultLanguage) {
      const defaultLangTranslation = translations.find(t => 
        t.language_code === defaultLanguage && t.name
      );
      
      if (defaultLangTranslation) return {
        name: defaultLangTranslation.name,
        language: defaultLanguage,
        isCurrentLanguage: false
      };
    }
    
    // If school's default language translation is not available, try English as a common fallback
    const englishTranslation = translations.find(t => 
      t.language_code === 'en' && t.name
    );
    
    if (englishTranslation) return {
      name: englishTranslation.name,
      language: 'en',
      isCurrentLanguage: false
    };
    
    // If no prioritized language is found, just take the first one with a name
    const firstAvailable = translations.find(t => t.name);
    if (firstAvailable) return {
      name: firstAvailable.name,
      language: firstAvailable.language_code,
      isCurrentLanguage: false
    };
    
    return null;
  }, [i18n.language, defaultLanguage]);
  
  // Helper function to find a translation in any language for a special name
  const findAvailableTranslationSpecialName = useCallback((translations) => {
    if (!translations || translations.length === 0) return null;
    
    // First try to find a translation in the current language
    const currentLangTranslation = translations.find(t => 
      t.language_code === i18n.language && t.name
    );
    
    if (currentLangTranslation) return {
      name: currentLangTranslation.name,
      language: i18n.language,
      isCurrentLanguage: true
    };
    
    // Next, try to find a translation in the school's default language
    if (defaultLanguage) {
      const defaultLangTranslation = translations.find(t => 
        t.language_code === defaultLanguage && t.name
      );
      
      if (defaultLangTranslation) return {
        name: defaultLangTranslation.name,
        language: defaultLanguage,
        isCurrentLanguage: false
      };
    }
    
    // If school's default language translation is not available, try English as a common fallback
    const englishTranslation = translations.find(t => 
      t.language_code === 'en' && t.name
    );
    
    if (englishTranslation) return {
      name: englishTranslation.name,
      language: 'en',
      isCurrentLanguage: false
    };
    
    // If no prioritized language is found, just take the first one with a name
    const firstAvailable = translations.find(t => t.name);
    if (firstAvailable) return {
      name: firstAvailable.name,
      language: firstAvailable.language_code,
      isCurrentLanguage: false
    };
    
    return null;
  }, [i18n.language, defaultLanguage]);
  
  // Handle confirming dish change
  const handleConfirmDishChange = async () => {
    try {
      setLoading(true);
      
      // Destructure modalState for easier access
      const { dayId, courseId, dayNumber, isSpecialName } = modalState;
      
      // Log the initial values for debugging
      console.log('Attempting to find menu item with these values:', {
        dayId,
        courseId,
        dayNumber,
        isSpecialName,
        menuId
      });
      
      // Ensure we have numeric values for comparison
      const dayNumberInt = parseInt(dayNumber, 10);
      const courseIdInt = courseId ? parseInt(courseId, 10) : null;
      
      // Skip API calls if there's no dayId (new menu)
      if (dayId) {
        let apiCallSuccessful = false;
        
        if (isSpecialName) {
          try {
          // Use the mutation hook to delete special name
          await deleteMenuItemMutation.mutateAsync({ 
            dayId, 
            courseId: null // Use null for special names
          });
            apiCallSuccessful = true;
          } catch (err) {
            console.error('Failed to delete special name:', err);
            setError('Failed to remove special name. Please try again.');
          }
        } else if (courseId) {
          // Log all days for debugging
          console.log('All days in formData:', formData.days.map(d => ({
            dayNumber: d.dayNumber,
            id: d.id,
            items: d.items.map(i => ({ courseId: i.courseId, id: i.id, dishId: i.dishId }))
          })));
          
          // Find the day and item in our form data - use toString for consistent type comparison
          const day = formData.days.find(d => d.dayNumber.toString() === dayNumberInt.toString());
          console.log('Found day:', day);
          
          if (day && day.items) {
            const item = day.items.find(i => i.courseId && i.courseId.toString() === courseIdInt.toString());
            console.log('Found item:', item);
            
            // Check if we have a valid menu item ID
            if (item && item.id) {
              try {
          // Use mutation hook for optimistic updates when clearing a dish
          await clearMenuItemDishMutation.mutateAsync({
            menuId,
            dayId,
                  itemId: item.id // Use the correct menu item ID
                });
                console.log('Successfully cleared dish from menu item with ID:', item.id);
                apiCallSuccessful = true;
              } catch (err) {
                console.error('Failed to clear menu item dish:', err);
                setError('Failed to remove dish. Please try again.');
              }
            } else {
              // If no itemId is found, we can't proceed with the API call
              console.error(`No menu item ID found for day ${dayNumberInt}, course ${courseIdInt}.`);
              setError('Unable to identify the menu item to clear. Please try again or refresh the page.');
            }
          } else {
            console.error(`Cannot find day with number ${dayNumberInt} in formData`);
            setError('Day information not found. Please try again or refresh the page.');
          }
        }
        
        // Only invalidate cache and update UI if API call was successful
        if (apiCallSuccessful) {
          // Invalidate all relevant caches to ensure all views are updated
          console.log('Invalidating caches for menu updates');
          
          // Invalidate specific menu cache
          queryClient.invalidateQueries(['menu', menuId, language]);
          
          // Invalidate all menus list cache
          queryClient.invalidateQueries('menus');
          
          // Also invalidate public menu data for all language variants
          queryClient.invalidateQueries({
            predicate: (query) => 
              query.queryKey[0] === 'publicMenu' && 
              (query.queryKey[1] === school?.subdomain || !query.queryKey[1])
          });
          
          // Update the form data to remove the dish/special name from the UI
          // But only if the API call was successful
          if (isSpecialName) {
            setFormData(prev => {
              const newDays = prev.days.map(day => {
                if (day.dayNumber === dayNumberInt) {
                  return {
                    ...day,
                    specialName: '',
                    specialNameId: null
                  };
                }
                return day;
              });
              
              return {
                ...prev,
                days: newDays
              };
            });
      } else {
            setFormData(prev => {
              const newDays = prev.days.map(day => {
                if (day.dayNumber === dayNumberInt) {
                  return {
                    ...day,
                    items: day.items.map(item => {
                      if (item.courseId && item.courseId.toString() === courseIdInt.toString()) {
                        return {
                          ...item,
                          dishId: null,
                          dishName: ''
                        };
                      }
                      return item;
                    })
                  };
                }
                return day;
              });
              
              return {
                ...prev,
                days: newDays
              };
            });
          }
        }
      } else {
        console.log('Skipping API call for new menu (no dayId exists yet)');
        
        // For new menus, we can still update the UI without API calls
      if (isSpecialName) {
        setFormData(prev => {
          const newDays = prev.days.map(day => {
              if (day.dayNumber === dayNumberInt) {
              return {
                ...day,
                specialName: '',
                specialNameId: null
              };
            }
            return day;
          });
          
          return {
            ...prev,
            days: newDays
          };
        });
      } else {
        setFormData(prev => {
          const newDays = prev.days.map(day => {
              if (day.dayNumber === dayNumberInt) {
              return {
                ...day,
                items: day.items.map(item => {
                    if (item.courseId && item.courseId.toString() === courseIdInt.toString()) {
                    return {
                      ...item,
                      dishId: null,
                      dishName: ''
                    };
                  }
                  return item;
                })
              };
            }
            return day;
          });
          
          return {
            ...prev,
            days: newDays
          };
        });
        }
      }
      
      // Always close the modal
      setModalState({ isOpen: false, dayNumber: null, courseId: null, dayId: null, isSpecialName: false });
    } catch (err) {
      console.error('Failed to delete menu item:', err);
      setError('Failed to remove dish. Please try again.');
      // Still close the modal even if there's an error
      setModalState({ isOpen: false, dayNumber: null, courseId: null, dayId: null, isSpecialName: false });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle canceling the dish change
  const handleCancelDishChange = () => {
    // Reset the modal state completely
    setModalState({ 
      isOpen: false, 
      dayNumber: null, 
      courseId: null, 
      dayId: null,
      isSpecialName: false
    });
  };
  
  // Handle submitting the form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use current_school.id if available, fall back to school_id for backward compatibility
    const schoolId = getSchoolId();
    
    if (!schoolId) {
      setError('No school ID found. Please log in again.');
      return;
    }
    
    try {
      setSaving(true);
      
      // Keep track of newly created dishes and their assignments
      const newDishAssignments = [];
      
      // Process any new dishes that haven't been created yet
      const processNewDishes = async () => {
        for (const day of formData.days) {
          // Process dishes for menu items
          for (const item of day.items) {
            if (item.dishName && !item.dishId) {
              const newDish = await handleCreateNewDish(day.dayNumber, item.courseId, item.dishName);
              if (newDish) {
                // Track this new dish and where it should be assigned
                newDishAssignments.push({
                  dayNumber: day.dayNumber,
                  courseId: item.courseId,
                  dishId: newDish.id
                });
              }
            }
          }
          
          // Process special name if needed
          if (day.specialName && !day.specialNameId) {
            console.log(`Creating special name for day ${day.dayNumber}: ${day.specialName}`);
            const newSpecialName = await handleCreateNewSpecialName(day.dayNumber, day.specialName);
            if (newSpecialName) {
              // Track this new special name
              newDishAssignments.push({
                dayNumber: day.dayNumber,
                courseId: null, // Special names use null courseId
                dishId: newSpecialName.id
              });
            }
          }
        }
      };
      
      await processNewDishes();
      
      // Create a clone of formData that we can safely modify with newly created dishes
      const updatedFormData = JSON.parse(JSON.stringify(formData));
      
      // Apply all the dish assignments to our cloned formData
      for (const assignment of newDishAssignments) {
        const dayIndex = updatedFormData.days.findIndex(day => day.dayNumber === assignment.dayNumber);
        if (dayIndex !== -1) {
          if (assignment.courseId === null) {
            // This is a special name
            updatedFormData.days[dayIndex].specialNameId = assignment.dishId;
          } else {
            // This is a regular dish
            const itemIndex = updatedFormData.days[dayIndex].items.findIndex(item => 
              item.courseId === assignment.courseId
            );
            if (itemIndex !== -1) {
              updatedFormData.days[dayIndex].items[itemIndex].dishId = assignment.dishId;
            }
          }
        }
      }
      
      // Prepare the menu data for saving - now using our updated data with dish assignments
      const menuData = {
        schoolId: schoolId,
        menuTypeId: updatedFormData.menuTypeId,
        month: updatedFormData.month + '-01', // Send YYYY-MM-DD format
        days: updatedFormData.days.map(day => {
          const dayData = {
            day_number: day.dayNumber,
            // Send special_name_id directly for the new schema
            special_name_id: day.specialNameId,
            items: day.items
              .filter(item => item.dishId || item.dishName)
              .map(item => ({
                course_id: item.courseId,
                dish_id: item.dishId
              }))
          };
          
          return dayData;
        })
      };
      
      console.log('Sending menu data:', menuData);
      
      let createdOrUpdatedMenu;
      
      if (isEditing) {
        // Use React Query mutation for update
        createdOrUpdatedMenu = await updateMenuMutation.mutateAsync({ 
          menuId, 
          menuData 
        });
        
        // Manually invalidate the specific menu query to force a refresh
        queryClient.invalidateQueries(['menu', menuId, language]);
        
        // Also invalidate public menu data - specifically include the subdomain
        queryClient.invalidateQueries({
          predicate: (query) => 
            query.queryKey[0] === 'publicMenu' && 
            (query.queryKey[1] === school?.subdomain || !query.queryKey[1])
        });
        
        setSuccess(t('admin.menuUpdated'));
        setIsNotificationVisible(true);
        setNotificationAnimation('fadeIn');
      } else {
        // Use React Query mutation for create
        createdOrUpdatedMenu = await createMenuMutation.mutateAsync(menuData);
        
        console.log('Created new menu with ID:', createdOrUpdatedMenu?.id);
        
        // Debug cache state before any explicit operations
        if (schoolId) {
          const menusBeforeCache = queryClient.getQueryData(['menus', schoolId, language]);
          console.log('[CacheDebug] Menus cache BEFORE any explicit updates in MenuEditor:', menusBeforeCache);
        }
        
        // Manually invalidate relevant queries
        queryClient.invalidateQueries(['menus', schoolId]);
        
        // Also invalidate public menu data - more specifically by subdomain
        queryClient.invalidateQueries({
          predicate: (query) => 
            query.queryKey[0] === 'publicMenu' && 
            (query.queryKey[1] === school?.subdomain || !query.queryKey[1])
        });
        
        // Ensure menus cache is updated by forcing a refetch
        if (schoolId) {
          console.log('[CacheDebug] Forcing refetch of menus data to ensure cache is updated');
          await queryClient.refetchQueries({
            queryKey: ['menus', schoolId, language],
            exact: true
          });
          
          // Debug log of what's now in the cache
          const menusCache = queryClient.getQueryData(['menus', schoolId, language]);
          console.log('[CacheDebug] Current menus cache after refetch in MenuEditor:', menusCache);
          
          // Check if our newly created menu is in the cache
          const newMenuInCache = menusCache?.find(m => m.id === createdOrUpdatedMenu?.id);
          console.log('[CacheDebug] Is new menu in MenuEditor cache after refetch?', !!newMenuInCache, newMenuInCache);
        }
        
        // If we have a menuId from created menu, update URL without page navigation
        if (createdOrUpdatedMenu && createdOrUpdatedMenu.id) {
          // Update URL
          const menuEditorUrl = getMenuEditorUrl(school?.subdomain, createdOrUpdatedMenu.id);
          window.history.replaceState(null, '', menuEditorUrl);
          
          // Set the menuId param to the new ID - this will make isEditing true
          navigate(menuEditorUrl, { replace: true });
          
          // Manually invalidate the specific menu query for the new ID
          queryClient.invalidateQueries(['menu', createdOrUpdatedMenu.id, language]);
          
          // Force a prefetch of the new menu data to ensure it's in the cache
          await queryClient.prefetchQuery({
            queryKey: ['menu', createdOrUpdatedMenu.id, language],
            queryFn: () => menuService.getMenu(createdOrUpdatedMenu.id, language)
          });
        }
        
        setSuccess(t('admin.menuCreated'));
        setIsNotificationVisible(true);
        setNotificationAnimation('fadeIn');
      }
      
      // Force a refetch of the menu data to ensure we have the latest
      if (isEditing) {
        queryClient.refetchQueries({
          queryKey: ['menu', menuId, language]
        });
      } else if (createdOrUpdatedMenu?.id) {
        queryClient.refetchQueries({
          queryKey: ['menu', createdOrUpdatedMenu.id, language]
        });
      }
      
      // Reset changes status
      const resetEvent = new CustomEvent('formChanged', { 
        detail: { hasChanges: false }
      });
      window.dispatchEvent(resetEvent);
      
      // Reset form state to match saved data
      setOriginalFormData(JSON.stringify(updatedFormData));
      setFormData(updatedFormData);
      setHasChanges(false);
      
      // Add debug logs for cache state
      console.log('Cache after save:', {
        menuQueryKey: ['menu', isEditing ? menuId : createdOrUpdatedMenu?.id, language],
        cacheData: queryClient.getQueryData(['menu', isEditing ? menuId : createdOrUpdatedMenu?.id, language])
      });
      
      // Auto-hide success message after 2 seconds with fade-out animation
      setTimeout(() => {
        setNotificationAnimation('fadeOut');
        // Wait for animation to complete before hiding
        setTimeout(() => {
          setIsNotificationVisible(false);
          setSuccess(null);
        }, 300); // Animation duration
      }, 2000);
    } catch (err) {
      console.error('Failed to save menu:', err);
      setError('Failed to save menu. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  // Function to dismiss notification with animation
  const dismissNotification = () => {
    setNotificationAnimation('fadeOut');
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsNotificationVisible(false);
      setSuccess(null);
    }, 300); // Animation duration
  };
  
  // Handle click outside autocomplete - update to handle both types of autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If we're switching tabs/windows, don't close the autocomplete
      if (!isWindowVisible) return;
      
      // Check if click is outside dish autocomplete
      const isDishClickOutside = !Object.values(autocompleteRefs.current).some(
        ref => ref && ref.contains(event.target)
      );
      
      // Check if click is outside special name autocomplete
      const isSpecialNameClickOutside = !Object.values(specialNameAutocompleteRefs.current).some(
        ref => ref && ref.contains(event.target)
      );
      
      // Close autocompletes if click is outside
      if (isDishClickOutside) {
        setShowAutocomplete({});
      }
      
      if (isSpecialNameClickOutside) {
        setShowSpecialNameAutocomplete({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isWindowVisible]);
  
  // Enhanced moveToNextInput function to skip readonly inputs
  const moveToNextInput = (currentDayNumber, currentCourseId = null) => {
    // Get all days that are actually displayed in the current month view
    const visibleDays = calendar.flatMap(week => week.filter(day => day !== null));
    
    // Sort days by dayNumber to ensure correct order
    const sortedDays = [...visibleDays].sort((a, b) => a.dayNumber - b.dayNumber);
    
    // Return early if there are no days to navigate
    if (sortedDays.length === 0) return;
    
    // Helper function to check if an input is readonly
    const isInputReadonly = (dayNumber, courseId) => {
      if (!courseId) return false; // Special name inputs are not readonly
      
      const day = formData.days.find(d => d.dayNumber === dayNumber);
      if (!day) return false;
      
      const item = day.items.find(i => i.courseId === courseId);
      if (!item) return false;
      
      // If the item has dishId but no dishName in current language, it's readonly
      return item.dishId && (!item.dishName || item.dishName === '');
    };
    
    // Helper function to find and focus the next valid input
    const findAndFocusNextInput = (startDayIndex, startCourseIndex = null) => {
      const courseIds = courses.map(c => c.id);
      let currentDayIndex = startDayIndex;
      let currentCourseIndex = startCourseIndex;
      
      // Loop through days and courses until we find a non-readonly input
      while (currentDayIndex < sortedDays.length) {
        const currentDay = sortedDays[currentDayIndex];
        
        if (currentCourseIndex === null) {
          // Try to focus special name first
          const specialNameKey = `day-${currentDay.dayNumber}`;
          const specialNameInput = specialNameInputRefs.current[specialNameKey];
          
          if (specialNameInput) {
            specialNameInput.focus();
            specialNameInput.select();
            return true;
          }
          
          // If no special name input, start with the first course
          currentCourseIndex = 0;
        }
        
        // Try each course for the current day
        while (currentCourseIndex < courseIds.length) {
          const courseId = courseIds[currentCourseIndex];
          const inputKey = `${currentDay.dayNumber}-${courseId}`;
          const inputElement = dishInputRefs.current[inputKey];
          
          // Skip readonly inputs
          if (!isInputReadonly(currentDay.dayNumber, courseId) && inputElement) {
            inputElement.focus();
            inputElement.select();
            return true;
          }
          
          currentCourseIndex++;
        }
        
        // Move to the next day
        currentDayIndex++;
        currentCourseIndex = null;
      }
      
      return false; // No non-readonly input found
    };
    
    if (currentCourseId === null) {
      // We're on a special name input, move to the first course of the same day
      // First, find the index of the current day
      const currentDayIndex = sortedDays.findIndex(day => day.dayNumber === currentDayNumber);
      if (currentDayIndex === -1) return;
      
      // Try to find a non-readonly input starting with the first course of the current day
      if (!findAndFocusNextInput(currentDayIndex, 0)) {
        // If no valid input found from this point, we could optionally wrap around
        // to the beginning of the form, but for now we'll just stop
      }
    } else {
      // We're on a course input, find the current day and course indices
      const currentDayIndex = sortedDays.findIndex(day => day.dayNumber === currentDayNumber);
      if (currentDayIndex === -1) return;
      
      const courseIds = courses.map(c => c.id);
      const currentCourseIndex = courseIds.findIndex(id => id === currentCourseId);
      if (currentCourseIndex === -1) return;
      
      // Try to find a non-readonly input starting with the next course
      if (!findAndFocusNextInput(currentDayIndex, currentCourseIndex + 1)) {
        // If no valid input found from this point, we could optionally wrap around
        // to the beginning of the form, but for now we'll just stop
      }
    }
  };
  
  // Helper function to scroll dropdown to keep focused item visible
  const scrollToFocusedItem = useCallback((dropdownElement, focusedIndex, hasCreateOption, dropdownPosition) => {
    if (!dropdownElement || focusedIndex < 0) return;
    
    // Get all selectable items in the dropdown
    const items = dropdownElement.querySelectorAll('[role="option"], .px-4.py-2');
    if (items.length === 0) return;
    
    // Find the actual DOM element for the focused item
    let targetItem = null;
    
    if (dropdownPosition === 'top') {
      // For top dropdowns, items are in reversed order
      if (hasCreateOption) {
        // Create option is at the bottom (visually first)
        if (focusedIndex === 0) {
          targetItem = items[items.length - 1]; // Last DOM element (create option)
        } else {
          // Regular items are in reverse order
          const reverseIndex = items.length - 1 - focusedIndex;
          targetItem = items[reverseIndex];
        }
      } else {
        // No create option, items are just reversed
        const reverseIndex = items.length - 1 - focusedIndex;
        targetItem = items[reverseIndex];
      }
    } else {
      // For bottom dropdowns, items are in normal order
      targetItem = items[focusedIndex];
    }
    
    if (!targetItem) return;
    
    // Calculate scroll position to keep item visible
    const dropdownRect = dropdownElement.getBoundingClientRect();
    const itemRect = targetItem.getBoundingClientRect();
    const dropdownScrollTop = dropdownElement.scrollTop;
    
    // Calculate the item's position relative to the dropdown's scroll container
    const itemTop = itemRect.top - dropdownRect.top + dropdownScrollTop;
    const itemBottom = itemTop + itemRect.height;
    
    // Check if item is above visible area
    if (itemTop < dropdownScrollTop) {
      dropdownElement.scrollTop = itemTop;
    }
    // Check if item is below visible area
    else if (itemBottom > dropdownScrollTop + dropdownRect.height) {
      dropdownElement.scrollTop = itemBottom - dropdownRect.height;
    }
  }, []);

  // Handle keyboard navigation for dish autocomplete
  const handleDishKeyDown = (e, dayNumber, courseId) => {
    const key = `${dayNumber}-${courseId}`;
    
    // Check if autocomplete is visible
    const isAutocompleteVisible = showAutocomplete[key];
    const results = autocompleteResults[key] || [];
    
    // Check if we have an exact match that would hide the create option
    const day = formData.days.find(d => d.dayNumber === dayNumber);
    const item = day?.items.find(i => i.courseId === courseId);
    const hasExactMatch = results.some(dish => 
      dish.name && item?.dishName && 
      dish.name.toLowerCase() === item.dishName.toLowerCase()
    );
    
    // Adjust maxIndex based on whether we show the create option
    const maxIndex = hasExactMatch ? results.length : results.length + 1;
    const currentIndex = focusedDishIndex[key] !== undefined ? focusedDishIndex[key] : -1;
    const dropdownPosition = dropdownPositions[key] || 'bottom';
    
    switch (e.key) {
      case 'ArrowDown':
        // Only handle arrow keys if autocomplete is visible
        if (!isAutocompleteVisible) return;
        
        e.preventDefault(); // Prevent cursor movement in input
        
        // Invert navigation direction for top-positioned dropdowns
        if (dropdownPosition === 'top') {
          // When dropdown is at the top, ArrowDown should move up the list (toward the input)
          if (currentIndex <= 0) {
            setFocusedDishIndex(prev => ({
              ...prev,
              [key]: maxIndex - 1
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = dishDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, maxIndex - 1, !hasExactMatch, dropdownPosition);
            }, 0);
          } else {
            setFocusedDishIndex(prev => ({
              ...prev,
              [key]: currentIndex - 1
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = dishDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, currentIndex - 1, !hasExactMatch, dropdownPosition);
            }, 0);
          }
        } else {
          // Standard behavior for bottom-positioned dropdowns
        if (currentIndex === -1) {
          setFocusedDishIndex(prev => ({
            ...prev,
            [key]: 0
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = dishDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, 0, !hasExactMatch, dropdownPosition);
          }, 0);
        } else {
          const nextIndex = (currentIndex + 1) % maxIndex;
          setFocusedDishIndex(prev => ({
            ...prev,
            [key]: nextIndex
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = dishDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, nextIndex, !hasExactMatch, dropdownPosition);
          }, 0);
          }
        }
        break;
      case 'ArrowUp':
        // Only handle arrow keys if autocomplete is visible
        if (!isAutocompleteVisible) return;
        
        e.preventDefault(); // Prevent cursor movement in input
        
        // Invert navigation direction for top-positioned dropdowns
        if (dropdownPosition === 'top') {
          // When dropdown is at the top, ArrowUp should move down the list (away from the input)
          if (currentIndex === -1) {
            setFocusedDishIndex(prev => ({
              ...prev,
              [key]: 0
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = dishDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, 0, !hasExactMatch, dropdownPosition);
            }, 0);
          } else {
            const nextIndex = (currentIndex + 1) % maxIndex;
            setFocusedDishIndex(prev => ({
              ...prev,
              [key]: nextIndex
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = dishDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, nextIndex, !hasExactMatch, dropdownPosition);
            }, 0);
          }
        } else {
          // Standard behavior for bottom-positioned dropdowns
        if (currentIndex <= 0) {
          setFocusedDishIndex(prev => ({
            ...prev,
            [key]: maxIndex - 1
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = dishDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, maxIndex - 1, !hasExactMatch, dropdownPosition);
          }, 0);
        } else {
          setFocusedDishIndex(prev => ({
            ...prev,
            [key]: currentIndex - 1
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = dishDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, currentIndex - 1, !hasExactMatch, dropdownPosition);
          }, 0);
          }
        }
        break;
      case 'Enter':
        e.preventDefault(); // Prevent form submission
        
        // If autocomplete is not visible or no option is selected, just move to next input
        if (!isAutocompleteVisible || currentIndex === -1) {
          moveToNextInput(dayNumber, courseId);
          return;
        }
        
        // Otherwise, handle the selected option
        if (currentIndex === 0 && !hasExactMatch) {
          // Create a new dish (now at index 0)
          const item = formData.days.find(d => d.dayNumber === dayNumber)?.items.find(i => i.courseId === courseId);
          if (item && item.dishName) {
            // Temporarily hide the autocomplete
            setShowAutocomplete({});
            // Move focus immediately before creating the dish
            moveToNextInput(dayNumber, courseId);
            // Create the dish in the background
            handleCreateNewDish(dayNumber, courseId, item.dishName)
              .catch(err => {
                console.error("Error creating dish:", err);
              });
          }
        } else {
          // Select an existing dish
          // If hasExactMatch, then currentIndex directly maps to results index
          // Otherwise, we need to subtract 1 to account for the create option
          const dishIndex = hasExactMatch ? currentIndex : currentIndex - 1;
          if (dishIndex >= 0 && dishIndex < results.length) {
            // Select the dish and hide autocomplete
            handleAutocompleteSelect(dayNumber, courseId, results[dishIndex]);
            // After selecting a dish, move focus to the next input
            setTimeout(() => moveToNextInput(dayNumber, courseId), 0);
          }
        }
        break;
      case 'Escape':
        // Close the autocomplete dropdown
        setShowAutocomplete(prev => ({
          ...prev,
          [key]: false
        }));
        setFocusedDishIndex(prev => ({
          ...prev,
          [key]: -1
        }));
        break;
      default:
        break;
    }
  };

  // Handle keyboard navigation for special name autocomplete
  const handleSpecialNameKeyDown = (e, dayNumber) => {
    const key = `day-${dayNumber}`;
    
    // Check if autocomplete is visible
    const isAutocompleteVisible = showSpecialNameAutocomplete[key];
    const results = specialNameAutocompleteResults[key] || [];
    
    // Check if we have an exact match that would hide the create option
    const day = formData.days.find(d => d.dayNumber === dayNumber);
    const hasExactMatch = results.some(specialName => 
      specialName.name && day?.specialName && 
      specialName.name.toLowerCase() === day.specialName.toLowerCase()
    );
    
    // Adjust maxIndex based on whether we show the create option
    const maxIndex = hasExactMatch ? results.length : results.length + 1;
    const currentIndex = focusedSpecialNameIndex[key] !== undefined ? focusedSpecialNameIndex[key] : -1;
    const dropdownPosition = dropdownPositions[key] || 'bottom';
    
    switch (e.key) {
      case 'ArrowDown':
        // Only handle arrow keys if autocomplete is visible
        if (!isAutocompleteVisible) return;
        
        e.preventDefault(); // Prevent cursor movement in input
        
        // Invert navigation direction for top-positioned dropdowns
        if (dropdownPosition === 'top') {
          // When dropdown is at the top, ArrowDown should move up the list (toward the input)
          if (currentIndex <= 0) {
            setFocusedSpecialNameIndex(prev => ({
              ...prev,
              [key]: maxIndex - 1
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = specialNameDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, maxIndex - 1, !hasExactMatch, dropdownPosition);
            }, 0);
          } else {
            setFocusedSpecialNameIndex(prev => ({
              ...prev,
              [key]: currentIndex - 1
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = specialNameDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, currentIndex - 1, !hasExactMatch, dropdownPosition);
            }, 0);
          }
        } else {
          // Standard behavior for bottom-positioned dropdowns
        if (currentIndex === -1) {
          setFocusedSpecialNameIndex(prev => ({
            ...prev,
            [key]: 0
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = specialNameDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, 0, !hasExactMatch, dropdownPosition);
          }, 0);
        } else {
          const nextIndex = (currentIndex + 1) % maxIndex;
          setFocusedSpecialNameIndex(prev => ({
            ...prev,
            [key]: nextIndex
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = specialNameDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, nextIndex, !hasExactMatch, dropdownPosition);
          }, 0);
          }
        }
        break;
      case 'ArrowUp':
        // Only handle arrow keys if autocomplete is visible
        if (!isAutocompleteVisible) return;
        
        e.preventDefault(); // Prevent cursor movement in input
        
        // Invert navigation direction for top-positioned dropdowns
        if (dropdownPosition === 'top') {
          // When dropdown is at the top, ArrowUp should move down the list (away from the input)
          if (currentIndex === -1) {
            setFocusedSpecialNameIndex(prev => ({
              ...prev,
              [key]: 0
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = specialNameDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, 0, !hasExactMatch, dropdownPosition);
            }, 0);
          } else {
            const nextIndex = (currentIndex + 1) % maxIndex;
            setFocusedSpecialNameIndex(prev => ({
              ...prev,
              [key]: nextIndex
            }));
            // Scroll to keep the newly focused item visible
            setTimeout(() => {
              const dropdownElement = specialNameDropdownRefs.current[key];
              scrollToFocusedItem(dropdownElement, nextIndex, !hasExactMatch, dropdownPosition);
            }, 0);
          }
        } else {
          // Standard behavior for bottom-positioned dropdowns
        if (currentIndex <= 0) {
          setFocusedSpecialNameIndex(prev => ({
            ...prev,
            [key]: maxIndex - 1
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = specialNameDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, maxIndex - 1, !hasExactMatch, dropdownPosition);
          }, 0);
        } else {
          setFocusedSpecialNameIndex(prev => ({
            ...prev,
            [key]: currentIndex - 1
          }));
          // Scroll to keep the newly focused item visible
          setTimeout(() => {
            const dropdownElement = specialNameDropdownRefs.current[key];
            scrollToFocusedItem(dropdownElement, currentIndex - 1, !hasExactMatch, dropdownPosition);
          }, 0);
          }
        }
        break;
      case 'Enter':
        e.preventDefault(); // Prevent form submission
        
        // If autocomplete is not visible or no option is selected, just move to next input
        if (!isAutocompleteVisible || currentIndex === -1) {
          moveToNextInput(dayNumber, null);
          return;
        }
        
        // Otherwise, handle the selected option
        if (currentIndex === 0 && !hasExactMatch) {
          // Create a new special name (now at index 0)
          const day = formData.days.find(d => d.dayNumber === dayNumber);
          if (day && day.specialName) {
            // Temporarily hide the autocomplete
            setShowSpecialNameAutocomplete({});
            // Move focus immediately before creating the special name
            moveToNextInput(dayNumber, null);
            // Create the special name in the background
            handleCreateNewSpecialName(dayNumber, day.specialName)
              .catch(err => {
                console.error("Error creating special name:", err);
              });
          }
        } else {
          // Select an existing special name
          // If hasExactMatch, then currentIndex directly maps to results index
          // Otherwise, we need to subtract 1 to account for the create option
          const specialNameIndex = hasExactMatch ? currentIndex : currentIndex - 1;
          if (specialNameIndex >= 0 && specialNameIndex < results.length) {
            // Select the special name and hide autocomplete
            handleSpecialNameAutocompleteSelect(dayNumber, results[specialNameIndex]);
            // After selecting a special name, move focus to the next input
            setTimeout(() => moveToNextInput(dayNumber, null), 0);
          }
        }
        break;
      case 'Escape':
        // Close the autocomplete dropdown
        setShowSpecialNameAutocomplete(prev => ({
          ...prev,
          [key]: false
        }));
        setFocusedSpecialNameIndex(prev => ({
          ...prev,
          [key]: -1
        }));
        break;
      default:
        break;
    }
  };

  // Special effect to handle language changes and reload courses
  useEffect(() => {
    // Use current_school.id if available, fall back to school_id for backward compatibility
    const schoolId = contextUser?.current_school?.id;
    
    // Skip if we don't have a menu type selected or user data
    if (!formData.menuTypeId || !schoolId) return;

    console.log(`Language changed to ${i18n.language}, reloading courses for menu type ${formData.menuTypeId}`);
    
    const reloadCourses = async () => {
      try {
        // Use current_school.id if available, fall back to school_id for backward compatibility
        const schoolId = contextUser?.current_school?.id;
        
        // Also fetch general courses for fallback
        const coursesData = await getCourses(schoolId, i18n.language);
        console.log('Fetched general courses in new language:', coursesData);
        setDbCourses(coursesData);
        
        // Fetch courses specific to this menu type in the new language
        const menuTypeCoursesData = await getMenuTypeCourses(formData.menuTypeId, i18n.language);
        console.log('Fetched menu type courses in new language:', menuTypeCoursesData);
        
        if (menuTypeCoursesData.length > 0) {
          // Update the courses immediately
          setMenuTypeCourses(menuTypeCoursesData);
        }
        
        // Force the coursesRef to reset on next render
        coursesRef.current = { language: i18n.language };
      } catch (error) {
        console.error('Failed to reload courses on language change:', error);
      }
    };
    
    reloadCourses();
  }, [i18n.language, formData.menuTypeId, contextUser]);

  // Special effect to handle language changes and reload menu types
  useEffect(() => {
    if (!contextUser) return;
    if (!schoolId) return;

    console.log(`Language changed to ${i18n.language}, reloading menu types`);
    
    const reloadMenuTypes = async () => {
      try {
        console.log(`[MenuTypeDebug] Reloading menu types for language: ${i18n.language}`);
        
        // Make a direct API call to fetch menu types
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const token = localStorage.getItem('accessToken');
        const url = `${API_BASE_URL}/api/admin/menu-types?schoolId=${schoolId}&language=${i18n.language}`;
        
        console.log(`[MenuTypeDebug] Direct API call to: ${url}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.error(`[MenuTypeDebug] API error: ${response.status}`);
          return;
        }
        
        const result = await response.json();
        console.log(`[MenuTypeDebug] Menu types reloaded successfully:`, result);
        
        // Update state directly with the fetched menu types
        if (result && result.length > 0) {
          setMenuTypes(result);
        }
        
        // Also invalidate related queries for courses
        queryClient.invalidateQueries({
          queryKey: ['menuTypeCourses', formData.menuTypeId]
        });
      } catch (error) {
        console.error('[MenuTypeDebug] Failed to reload menu types on language change:', error);
      }
    };
    
    reloadMenuTypes();
  }, [i18n.language, contextUser, schoolId, queryClient, formData.menuTypeId]);

  // Function to open delete menu modal
  const handleDeleteMenu = () => {
    setIsDeleteModalOpen(true);
  };
  
  // Handle successful menu deletion
  const handleDeleteSuccess = () => {
    // Immediately invalidate and remove queries for the deleted menu
    queryClient.removeQueries(['menu', menuId]);
    queryClient.removeQueries({
      predicate: (query) => 
        query.queryKey[0] === 'publicMenu' && 
        (query.queryKey[1] === school?.subdomain || !query.queryKey[1])
    });
    queryClient.invalidateQueries(['menus', schoolId]);
    
    // Then navigate
    navigate(getAdminUrl(school?.subdomain));
    toast.success(t('admin.menuDeleted'));
  };

  // Effect to check for form data reset flag from edit->create transition
  useEffect(() => {
    console.log(`[Effect ClearForm] Running. isEditing=${isEditing}`);
    const shouldClearForm = sessionStorage.getItem('clearMenuFormData');
    console.log(`[Effect ClearForm] clearMenuFormData flag: ${shouldClearForm}`);
    
    if (shouldClearForm === 'true' && !isEditing) {
      console.log('[Effect ClearForm] Clearing form data for transition to create mode');
      
      // First reset originalFormData so the init effect won't skip
      setOriginalFormData(null);
      console.log('[Effect ClearForm] Reset originalFormData');
      
      // Create new empty days array with current month and menu type
      // Only clear days initially, then let the initialization effect create new days
      setFormData(prev => {
        console.log(`[Effect ClearForm] Resetting days array. Current month=${prev.month}, menuTypeId=${prev.menuTypeId}`);
        return {
          ...prev,
          days: []
        };
      });
      
      // Clear the flag from session storage
      sessionStorage.removeItem('clearMenuFormData');
      console.log('[Effect ClearForm] Removed clearMenuFormData flag');
    } else {
      console.log(`[Effect ClearForm] Skipping clear.`);
    }
  }, [isEditing]);

  // Effect to initialize menu type ID from school's default or lowest available
  useEffect(() => {
    // Skip if we're editing an existing menu, if menuTypeId is already set, or if we've applied URL parameters
    if (isEditing || formData.menuTypeId !== null || locationStateAppliedRef.current) return;
    
    const initializeMenuTypeId = async () => {
      try {
        setLoading(true);
        console.log('Initializing menu type ID from school or lowest available');
        
        // First try to use the school's default menu type ID
        if (school?.default_menu_type_id) {
          console.log(`Using school's default menu type ID: ${school.default_menu_type_id}`);
          setFormData(prev => ({
            ...prev,
            menuTypeId: school.default_menu_type_id
          }));
          return;
        }
        
        // Get school ID using our helper function
        const schoolId = getSchoolId();
        
        // If no school default, fetch menu types and use the lowest ID
        if (schoolId) {
          const menuTypesData = await menuTypeService.getMenuTypes(schoolId, i18n.language);
          if (menuTypesData && menuTypesData.length > 0) {
            // Sort menu types by ID and use the lowest
            const lowestMenuTypeId = [...menuTypesData].sort((a, b) => a.id - b.id)[0].id;
            console.log(`No school default menu type, using lowest ID: ${lowestMenuTypeId}`);
            setFormData(prev => ({
              ...prev,
              menuTypeId: lowestMenuTypeId
            }));
          } else {
            // If no menu types available, default to 1 as a last resort
            console.log('No menu types found, defaulting to ID 1');
            setFormData(prev => ({
              ...prev,
              menuTypeId: 1
            }));
          }
        }
      } catch (err) {
        console.error('Error initializing menu type ID:', err);
        // Default to 1 in case of error
        setFormData(prev => ({
          ...prev,
          menuTypeId: 1
        }));
      } finally {
        setLoading(false);
      }
    };
    
    initializeMenuTypeId();
  }, [isEditing, school, user, i18n.language, formData.menuTypeId, locationStateAppliedRef]);
  
  // Keep the loading state during the initial rendering
  useEffect(() => {
    // Check for the MenuEditor component being fully ready
    const isFullyLoaded = 
      !isEditing && // Only for create mode
      formData.days.length > 0 && // Days array is populated
      menuTypeCourses.length > 0 && // Menu type courses are loaded
      formData.days[0]?.items && 
      formData.days[0]?.items.length > 0 && // Items array is populated
      formData.days[0]?.items[0]?.courseId === menuTypeCourses[0]?.id; // Courses are properly linked
      
    // Log the loading status
    if (loading && isFullyLoaded) {
      console.log('MenuEditor is fully initialized and ready to display');
      setLoading(false);
    }
  }, [loading, isEditing, formData.days, menuTypeCourses]);
  
  // Modify the createNewMenu function to properly handle loading state
  const createNewMenu = useCallback(() => {
    // Set loading state during initialization
    setLoading(true);
    console.log('Creating new menu - waiting for initialization...');
    
    // The rest of the loading state management happens in the effects above
  }, []);
  
  // Add a dedicated effect to fix mismatched course counts
  useEffect(() => {
    if (!isEditing && menuTypeCourses.length > 0 && formData.days.length > 0) {
      // Check the first day to see if its items count matches the courses count
      const firstDay = formData.days[0];
      if (firstDay && firstDay.items && firstDay.items.length !== menuTypeCourses.length) {
        console.log(`[MenuTypeDebug] Effect triggered: Fixing course count mismatch - days have ${firstDay.items.length} items, menu type ${formData.menuTypeId} has ${menuTypeCourses.length} courses`);
        
        // Regenerate all days with the correct number of courses
        const newDays = createEmptyDaysArray(formData.month, formData.menuTypeId);
        console.log('[MenuTypeDebug] Regenerated days with correct course count:', newDays);
        
        setFormData(prev => ({
          ...prev,
          days: newDays
        }));
      }
    }
  }, [menuTypeCourses, formData.days, formData.menuTypeId, formData.month, isEditing, createEmptyDaysArray]);
  
  // Effect to handle clearing form data when redirected from edit to create mode
  useEffect(() => {
    // Check for the clearMenuFormData flag in sessionStorage
    const shouldClearFormData = sessionStorage.getItem('clearMenuFormData') === 'true';
    console.log('[RedirectDebug] Found clearMenuFormData in sessionStorage:', shouldClearFormData);
    console.log('[RedirectDebug] Current isEditing state:', isEditing);
    
    if (shouldClearFormData && !isEditing) {
      console.log('[RedirectDebug] Clearing form data for transition to create mode');
      console.log('[RedirectDebug] Current pathname:', window.location.pathname);
      console.log('[RedirectDebug] Current search params:', window.location.search);
      
      try {
        // Parse query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const monthParam = urlParams.get('month');
        const menuTypeIdParam = urlParams.get('menuTypeId');
        
        console.log('[RedirectDebug] URL Parameters - month:', monthParam, 'menuTypeId:', menuTypeIdParam);
        
        // Reset form data but preserve URL parameters
        setFormData(prev => ({
          ...prev,
          month: monthParam || prev.month,
          menuTypeId: menuTypeIdParam ? parseInt(menuTypeIdParam) : prev.menuTypeId,
          days: []
        }));
        
        console.log('[RedirectDebug] Form data updated with month:', monthParam, 'menuTypeId:', menuTypeIdParam);
      } catch (error) {
        console.error('[RedirectDebug] Error during form data reset:', error);
      }
      
      // Clear the flag from session storage
      sessionStorage.removeItem('clearMenuFormData');
      console.log('[RedirectDebug] Removed clearMenuFormData from sessionStorage');
    }
  }, [isEditing]);
  
  // Effect to directly fetch menu types if React Query hook fails
  useEffect(() => {
    const fetchMenuTypes = async () => {
      if (!schoolId) return;
      
      try {
        console.log('[MenuTypeDebug] Manual fallback fetch of menu types');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const token = localStorage.getItem('accessToken');
        const url = `${API_BASE_URL}/api/admin/menu-types?schoolId=${schoolId}&language=${i18n.language}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.error(`[MenuTypeDebug] API error: ${response.status}`);
          return;
        }
        
        const result = await response.json();
        console.log('[MenuTypeDebug] Manual fetch successful, menuTypes:', result);
        
        if (result && result.length > 0) {
          setMenuTypes(result);
        }
      } catch (error) {
        console.error('[MenuTypeDebug] Error in manual menu types fetch:', error);
      }
    };
    
    // Only run this if menuTypes is empty after a few seconds
    if (menuTypes.length <= 1) {
      const timer = setTimeout(() => {
        fetchMenuTypes();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [schoolId, i18n.language, menuTypes.length]);
  
  
  // Add this debug effect
  useEffect(() => {
    // Log loading states for debugging
    console.log('[LoadingDebug] Loading states:', {
      // Menu Types
      isMenuTypesLoading,
      isMenuTypesFetching,
      isMenuTypesInitialLoading,
      // Dishes
      isDishesLoading,
      isDishesFetching,
      isDishesInitialLoading,
      // Courses
      isCoursesLoading,
      isCoursesFetching,
      isCoursesInitialLoading,
      // Menu Type Courses
      isMenuTypeCoursesLoading,
      isMenuTypeCoursesFetching,
      isMenuTypeCoursesInitialLoading,
      // Menu
      isMenuLoading,
      isMenuFetching,
      isMenuInitialLoading,
      // Special Names
      isSpecialNamesLoading,
      isSpecialNamesFetching,
      isSpecialNamesInitialLoading,
      // Overall loading state
      loading
    });
  }, [
    // Menu Types
    isMenuTypesLoading,
    isMenuTypesFetching,
    isMenuTypesInitialLoading,
    // Dishes
    isDishesLoading,
    isDishesFetching,
    isDishesInitialLoading,
    // Courses
    isCoursesLoading,
    isCoursesFetching,
    isCoursesInitialLoading,
    // Menu Type Courses
    isMenuTypeCoursesLoading,
    isMenuTypeCoursesFetching,
    isMenuTypeCoursesInitialLoading,
    // Menu
    isMenuLoading,
    isMenuFetching,
    isMenuInitialLoading,
    // Special Names
    isSpecialNamesLoading,
    isSpecialNamesFetching,
    isSpecialNamesInitialLoading,
    // Overall loading state
    loading
  ]);
  
  // Add this effect to ensure upward dropdowns are scrolled to the bottom
  useEffect(() => {
    // Skip if still loading since dropdowns won't be displayed
    if (loading) return;
    
    const checkAndScrollDropdowns = () => {
      // Handle special name autocomplete dropdowns
      Object.keys(showSpecialNameAutocomplete).forEach(key => {
        if (showSpecialNameAutocomplete[key] && dropdownPositions[key] === 'top') {
          const dropdownElement = specialNameDropdownRefs.current[key];
          if (dropdownElement) {
            // Scroll to the bottom for upward dropdowns
            dropdownElement.scrollTop = dropdownElement.scrollHeight;
          }
        }
      });

      // Handle dish autocomplete dropdowns
      Object.keys(showAutocomplete).forEach(key => {
        if (showAutocomplete[key] && dropdownPositions[key] === 'top') {
          const dropdownElement = dishDropdownRefs.current[key];
          if (dropdownElement) {
            // Scroll to the bottom for upward dropdowns
            dropdownElement.scrollTop = dropdownElement.scrollHeight;
          }
        }
      });
    };
    
    // Run initially and after a short delay to ensure DOM is updated
    checkAndScrollDropdowns();
    const timeoutId = setTimeout(checkAndScrollDropdowns, 50);
    
    return () => clearTimeout(timeoutId);
  }, [showSpecialNameAutocomplete, showAutocomplete, dropdownPositions, loading]);
  
  // Replace the loading div with LoadingScreen component
  if (loading) return (
    <>
      <style>{notificationStyle}</style>
      <Navbar isAdmin={true} />
      <LoadingScreen 
        message={isEditing ? t('admin.loadingMenu') : t('admin.preparingMenuEditor')}
        subMessage={t('admin.thisWillOnlyTakeAMoment')}
      />
    </>
  );
  
  // Special handler for month changes in edit mode to avoid auto-redirection to existing menus
  const handleMonthChange = async (newMonth) => {
    console.log(`[HandleMonthChange] Start. newMonth=${newMonth}, isEditing=${isEditing}`);
    const schoolId = getSchoolId();
    
    if (!schoolId) {
      console.warn('[HandleMonthChange] No school ID available.');
      return;
    }
    
    try {
      setLoading(true);
      const menuTypeId = formData.menuTypeId;
      console.log(`[HandleMonthChange] Checking menu: month=${newMonth}, menuTypeId=${menuTypeId}, schoolId=${schoolId}`);
      
      const matchingMenu = await menuService.checkExistingMenu(
        schoolId,
        newMonth, 
        menuTypeId
      );
      
      console.log(`[HandleMonthChange] checkExistingMenu response:`, matchingMenu);
      
      if (matchingMenu) {
        console.log(`[HandleMonthChange] Found existing menu ID: ${matchingMenu.id}`);
        if (matchingMenu.id !== parseInt(menuId)) {
          console.log(`[HandleMonthChange] Navigating to existing menu: ${matchingMenu.id}`);
          const targetUrl = getMenuEditorUrl(school?.subdomain, matchingMenu.id);
          console.log(`[HandleMonthChange] Navigate target URL (existing): ${targetUrl}`);
          navigate(targetUrl); // <--- Navigation call 1
        } else {
          console.log(`[HandleMonthChange] Same menu, updating month locally.`);
          setFormData(prev => ({ ...prev, month: newMonth }));
        }
      } else {
        console.log(`[HandleMonthChange] No matching menu found.`);
        console.log(`[HandleMonthChange] Navigating to menu creation for ${newMonth}, type ${menuTypeId}`);
        
        const date = new Date(newMonth + "-01");
        const yearParam = date.getFullYear().toString();
        const monthParam = (date.getMonth() + 1).toString().padStart(2, '0');
        const queryParams = { year: yearParam, month: monthParam, menuTypeId };
        
        const targetUrl = getMenuEditorUrl(school?.subdomain, null, queryParams);
        console.log(`[HandleMonthChange] Navigate target URL (create): ${targetUrl}`);
        
        // Set flags before navigating
        sessionStorage.setItem('clearMenuFormData', 'true');
        sessionStorage.setItem('skipExistingMenuCheck', 'true');
        
        // Reset locationStateAppliedRef to ensure new URL params are processed
        locationStateAppliedRef.current = false;
        
        console.log('[HandleMonthChange] Set sessionStorage flags: clearMenuFormData=true, skipExistingMenuCheck=true');
        console.log('[HandleMonthChange] Reset locationStateAppliedRef to false');

        navigate(targetUrl); // <--- Navigation call 2
      }
    } catch (error) {
      console.error(`[HandleMonthChange] Error:`, error);
    } finally {
      setLoading(false);
    }
  };
  
  // Special handler for menu type changes in edit mode to avoid auto-redirection issues
  const handleMenuTypeChange = async (newMenuTypeId) => {
    console.log(`[HandleMenuTypeChange] Start. newMenuTypeId=${newMenuTypeId}, isEditing=${isEditing}`);
    const schoolId = getSchoolId();
    
    if (!schoolId) {
      console.warn('[HandleMenuTypeChange] No school ID available.');
      return;
    }
    
    try {
      setLoading(true);
      console.log(`[HandleMenuTypeChange] Checking menu: month=${formData.month}, menuTypeId=${newMenuTypeId}, schoolId=${schoolId}`);
      
      const matchingMenu = await menuService.checkExistingMenu(
        schoolId,
        formData.month, 
        newMenuTypeId
      );
      
      console.log(`[HandleMenuTypeChange] checkExistingMenu response:`, matchingMenu);
      
      if (matchingMenu) {
        console.log(`[HandleMenuTypeChange] Found existing menu ID: ${matchingMenu.id}`);
        if (matchingMenu.id !== parseInt(menuId)) {
          console.log(`[HandleMenuTypeChange] Navigating to existing menu: ${matchingMenu.id}`);
          const targetUrl = getMenuEditorUrl(school?.subdomain, matchingMenu.id);
          console.log(`[HandleMenuTypeChange] Navigate target URL (existing): ${targetUrl}`);
          
          // Reset locationStateAppliedRef to ensure clean state on next render
          locationStateAppliedRef.current = false;
          console.log('[HandleMenuTypeChange] Reset locationStateAppliedRef to false');
          
          navigate(targetUrl);
        } else {
          console.log(`[HandleMenuTypeChange] Same menu, updating menuTypeId locally.`);
          setFormData(prev => ({ ...prev, menuTypeId: newMenuTypeId }));
        }
      } else {
        console.log(`[HandleMenuTypeChange] No matching menu found.`);
        console.log(`[HandleMenuTypeChange] Navigating to menu creation for ${formData.month}, type ${newMenuTypeId}`);
        
        const queryParams = { 
          month: formData.month.substring(5, 7), // Extract month part (MM)
          year: formData.month.substring(0, 4),  // Extract year part (YYYY)
          menuTypeId: newMenuTypeId 
        };
        
        const targetUrl = getMenuEditorUrl(school?.subdomain, null, queryParams);
        console.log(`[HandleMenuTypeChange] Navigate target URL (create): ${targetUrl}`);
        
        // Set flags before navigating
        sessionStorage.setItem('clearMenuFormData', 'true');
        sessionStorage.setItem('skipExistingMenuCheck', 'true');
        
        // Reset locationStateAppliedRef to ensure URL parameters are processed
        locationStateAppliedRef.current = false;
        
        console.log('[HandleMenuTypeChange] Set sessionStorage flags: clearMenuFormData=true, skipExistingMenuCheck=true');
        console.log('[HandleMenuTypeChange] Reset locationStateAppliedRef to false');

        navigate(targetUrl);
      }
    } catch (error) {
      console.error(`[HandleMenuTypeChange] Error:`, error);
    } finally {
      setLoading(false);
    }
  };
  
  // --- Log Component End ---
  console.log(`[MenuEditor Render] End. month=${formData.month}, menuTypeId=${formData.menuTypeId}`);
  // --- End Log ---

  // Handle opening the copy menu modal
  const handleCopyMenu = () => {
    setIsCopyModalOpen(true);
  };
  
  // Handle completion of the copy process
  const handleCopyComplete = (newMenuId) => {
    setIsCopyModalOpen(false);
    // Navigate to the new menu
    navigate(getMenuEditorUrl(school?.subdomain, newMenuId));
  };

  // *** START: Notes and Comments Modal Handlers - Step 3 ***
  
  // Helper function to get note text for a day
  function getNoteForDayFromData(dayId, formData, menuData) {
      // Find the day in the formData first
      const dayData = formData.days.find(d => d.id === dayId);
      // If menuData is available and has the day, check for note there
      const noteFromMenuData = menuData?.days?.find(d => d.id === dayId)?.note?.text; 
      return noteFromMenuData || ''; 
  }

  // Handler functions as plain functions, not useCallback
  function handleOpenNotesModal(dayId) {
    if (!dayId) {
        toast.error("Cannot edit notes for a day that hasn't been saved yet.");
        return;
    }
    setSelectedDayIdForModal(dayId);
    setCurrentNoteText(getNoteForDayFromData(dayId, formData, menuData));
    setIsNotesModalOpen(true);
  }

  function handleCloseNotesModal() {
    setIsNotesModalOpen(false);
    setSelectedDayIdForModal(null);
    setCurrentNoteText('');
  }

  async function handleSaveNote({ dayId, text }) {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500)); 
    toast.success('Note saved successfully!');
  }

  function handleOpenCommentsModal(dayId) {
    if (!dayId) {
        toast.error("Cannot view comments for a day that hasn't been saved yet.");
        return;
    }
    setSelectedDayIdForModal(dayId);
    setIsCommentsModalOpen(true);
  }

  function handleCloseCommentsModal() {
    setIsCommentsModalOpen(false);
    setSelectedDayIdForModal(null);
  }
  
  // Function to handle opening the copy week modal
  function handleOpenCopyWeekModal(dayNumber) {
    // Find the current week that this day belongs to
    const targetDay = formData.days.find(d => d.dayNumber === dayNumber);
    if (!targetDay) return;
    
    // Find which week this day belongs to in the calendar view
    let weekIndex = -1;
    let dayIndexInWeek = -1;
    
    // Search through the calendar weeks to find this day
    for (let i = 0; i < calendar.length; i++) {
      const dayIndex = calendar[i].findIndex(d => d && d.dayNumber === dayNumber);
      if (dayIndex !== -1) {
        weekIndex = i;
        dayIndexInWeek = dayIndex;
        break;
      }
    }
    
    if (weekIndex === -1) return; // Day not found in calendar
    
    // Get all days in this visual week (Mon-Fri)
    const weekDays = calendar[weekIndex].slice(0, 5); // Only take Mon-Fri
    
    // Format the sourceWeekDays data for the API
    const formattedSourceWeekDays = weekDays.map(day => {
      if (!day) return null; // Might be null for days outside current month
      
      // Calculate the date for this day based on month and day number
      const [year, month] = formData.month.split('-').map(n => parseInt(n, 10));
      const date = new Date(year, month - 1, day.dayNumber +1);
      
      return {
        dayNumber: day.dayNumber,
        date: date.toISOString().split('T')[0], // Format as YYYY-MM-DD
        items: day.items
      };
    }).filter(Boolean); // Remove null entries
    
    // Set the source week days for the modal
    setSourceWeekDays(formattedSourceWeekDays);
    
    // Open the modal
    setIsCopyWeekModalOpen(true);
  }
  
  // Handle completion of the copy week operation
  function handleCopyWeekComplete(targetMenuId, updatedMenuData) {
    console.log('[MenuEditor] handleCopyWeekComplete triggered', { 
      targetMenuId, 
      currentMenuIdFromUrl: menuId, // menuId is from useParams() at component top level
      updatedMenuData 
    });

    // Hooks like useQueryClient(), useSchool(), useTranslation(), useNavigate() must be called
    // at the TOP LEVEL of the MenuEditor component, not inside this function.
    // This function will use queryClient, school, i18n, t, and navigate from the component's scope.

    const currentLanguage = i18n.language; // i18n should be from useTranslation() at component top level
    const currentSchoolInstance = school; // school should be from useSchool() at component top level

    // Numeric ID of the menu page we are currently on
    const numericMenuIdFromUrl = menuId ? parseInt(menuId, 10) : null;
    console.log('[MenuEditor] Current menu ID from URL (numeric):', numericMenuIdFromUrl);

    // Basic validation of received data
    if (!updatedMenuData || !updatedMenuData.days || !Array.isArray(updatedMenuData.days)) {
      console.error('[MenuEditor] Invalid or incomplete updatedMenuData received in handleCopyWeekComplete:', updatedMenuData);
      toast.error(t('admin.copyWeek.errorProcessingResponse'));
      setIsCopyWeekModalOpen(false); // Close modal on error
      // Potentially call setSubmittingCopy(false) here if passed as a param
      return;
    }
    
    // Transform the received menu data (snake_case to camelCase, ensure all fields exist)
    // This transformation should match what useMenu hook would provide
    const transformedMenuData = {
      id: updatedMenuData.id,
      schoolId: updatedMenuData.school_id,
      menuTypeId: updatedMenuData.menu_type_id,
      month: parseInt(String(updatedMenuData.month), 10), // Ensure month is a number
      year: parseInt(String(updatedMenuData.year), 10),   // Ensure year is a number
      isPublished: updatedMenuData.is_published,
      daysCount: updatedMenuData.days_count,
      // Ensure days array is properly formatted
      days: updatedMenuData.days.map(day => ({
        id: day.id,
        day: day.day, // day number
        dayNumber: day.day_number || day.day, // ensure dayNumber for consistency
        date: day.date,
        isWeekend: day.is_weekend,
        isCurrentMonth: day.is_current_month,
        specialNameId: day.special_name_id,
        specialName: day.special_name,
        hasSpecialNameRequestedTranslation: day.has_special_name_requested_translation,
        hasUnreadComments: day.has_unread_comments,
        items: (day.items || []).map(item => ({
          id: item.id,
          menuItemId: item.menu_item_id || item.id, // ensure menuItemId for consistency
          courseId: item.course_id,
          courseName: item.course_name,
          dishId: item.dish_id,
          dishName: item.dish_name,
          displayOrder: item.display_order,
          hasRequestedTranslation: item.has_requested_translation,
        })).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)), // Sort items by display_order
        notes: day.notes || null, 
        comments: day.comments || [],
      })).sort((a,b) => a.day - b.day), // Sort days by day number
      createdAt: updatedMenuData.created_at,
      updatedAt: updatedMenuData.updated_at,
      timestamp: Date.now() // Add a timestamp for cache busting / rerender trigger
    };
    console.log('[MenuEditor] Transformed Menu Data for Cache/Form:', transformedMenuData);

    // --- Optimistic Cache Update for Target Menu ---
    if (targetMenuId && transformedMenuData) {
      console.log(`[MenuEditor] Optimistically updating React Query cache for targetMenuId: ${targetMenuId} with new data.`);
      queryClient.setQueryData(['menu', targetMenuId, currentLanguage], transformedMenuData);
      
      queryClient.invalidateQueries(['menus', currentSchoolInstance?.id, currentLanguage]);
      queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey[0] === 'publicMenu' && 
          (query.queryKey[1] === currentSchoolInstance?.subdomain || !query.queryKey[1])
      });
    }

    // --- UI Update Logic ---
    if (targetMenuId === numericMenuIdFromUrl || (!numericMenuIdFromUrl && targetMenuId === transformedMenuData.id)) { 
      console.log('[MenuEditor] Copy target is the current menu (or new menu just created by copy), updating form data.');
      
      setFormData({
        month: `${transformedMenuData.year}-${String(transformedMenuData.month).padStart(2, '0')}`,
        menuTypeId: transformedMenuData.menuTypeId,
        days: transformedMenuData.days,
        id: transformedMenuData.id,
        timestamp: transformedMenuData.timestamp 
      });

      setOriginalFormData(JSON.stringify(transformedMenuData));
      setHasChanges(false);
      
      const event = new CustomEvent('formChanged', { detail: { hasChanges: false } });
      window.dispatchEvent(event);

      toast.success(t('admin.copyWeek.success'));
      
    } else {
      console.log(`[MenuEditor] Copy target is a different menu (target: ${targetMenuId}, current URL menuId: ${numericMenuIdFromUrl}). Navigating.`);
      
      if (numericMenuIdFromUrl) {
         queryClient.invalidateQueries(['menu', numericMenuIdFromUrl, currentLanguage]);
      }

      navigate(getMenuEditorUrl(currentSchoolInstance?.subdomain, targetMenuId));
      // Toast for navigation might be good if navigation takes a moment or for confirmation
      // toast.success(t('admin.copyWeek.successNavigating')); // Already have a general success
    }
    
    setIsCopyWeekModalOpen(false);
    // If setSubmittingCopy was passed from CopyWeekModal, call it here:
    // e.g., if (typeof setSubmittingCopy === 'function') setSubmittingCopy(false);
  }

  // Function to handle opening the copy day modal
  function handleOpenCopyDayModal(dayNumber) {
    // Find the day in formData
    const targetDay = formData.days.find(d => d.dayNumber === dayNumber);
    if (!targetDay) return;
    
    // Format the source day data for the API
    const [year, month] = formData.month.split('-').map(n => parseInt(n, 10));
    
    // Fix: Create date based on year, month, and exact day number
    // The dayNumber is 1-based (1st of the month), but months in JS Date are 0-based
    const date = new Date(year, month - 1, targetDay.dayNumber);
    
    console.log(`[MenuEditor] Creating sourceDayData for day ${dayNumber}:`, {
      year,
      month,
      dayNumber: targetDay.dayNumber,
      resultingDate: date.toISOString().split('T')[0]
    });
    
    const formattedSourceDayData = {
      dayNumber: targetDay.dayNumber,
      date: date.toISOString().split('T')[0], // Format as YYYY-MM-DD
      items: targetDay.items
    };
    
    // Set the source day data for the modal
    setSourceDayData(formattedSourceDayData);
    
    // Open the modal
    setIsCopyDayModalOpen(true);
  }
  
  // Handle completion of the copy day operation
  function handleCopyDayComplete(targetMenuId, updatedMenuData) {
    console.log('[MenuEditor] handleCopyDayComplete triggered', { 
      targetMenuId, 
      updatedMenuData: updatedMenuData ? 'present' : 'missing' 
    });
    
    // Restore the missing variable declarations
    const currentLanguage = i18n.language;
    const currentSchoolInstance = school;
    
    // Numeric ID of the menu page we are currently on
    const numericMenuIdFromUrl = menuId ? parseInt(menuId, 10) : null;
    console.log('[MenuEditor] Current menu ID from URL (numeric):', numericMenuIdFromUrl);
    
    if (!updatedMenuData) {
      console.error('[MenuEditor] No menu data received from copy operation');
      setIsCopyDayModalOpen(false);
      return;
    }
    
    // Transform the backend response to match frontend expectations
    const transformedMenuData = {
      id: updatedMenuData.id,
      schoolId: updatedMenuData.school_id,
      menuTypeId: updatedMenuData.menu_type_id,
      month: `${updatedMenuData.year}-${String(updatedMenuData.month).padStart(2, '0')}`,
      year: updatedMenuData.year,
      isPublished: updatedMenuData.is_published,
      // Transform days array to match frontend structure
      days: updatedMenuData.days.map(day => ({
        id: day.id,
        dayNumber: day.day || day.dayNumber, // Backend uses "day", frontend expects "dayNumber"
        date: day.date,
        isWeekend: day.is_weekend,
        isCurrentMonth: day.is_current_month,
        specialNameId: day.specialNameId || day.special_name_id,
        specialName: day.specialName || day.special_name || '',
        hasSpecialNameRequestedTranslation: day.has_special_name_requested_translation,
        hasUnreadComments: day.has_unread_comments,
        items: (day.items || []).map(item => ({
          id: item.id,
          menuItemId: item.menu_item_id || item.id,
          courseId: item.course_id,
          courseName: item.course_name || '',
          dishId: item.dish_id,
          dishName: item.dish_name || '',
          displayOrder: item.display_order || item.course_display_order,
          hasRequestedTranslation: item.has_requested_translation,
        })).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
        notes: day.notes || null,
        comments: day.comments || [],
      })).sort((a, b) => a.dayNumber - b.dayNumber),
      createdAt: updatedMenuData.created_at,
      updatedAt: updatedMenuData.updated_at,
      timestamp: Date.now() // Add timestamp for cache busting
    };
    
    console.log('[MenuEditor] Transformed menu data:', transformedMenuData);
    
    // Optimistically update the React Query cache for the target menu
    const numericTargetMenuId = parseInt(targetMenuId, 10);
    
    if (isNaN(numericTargetMenuId)) {
      console.error('[MenuEditor] Invalid targetMenuId:', targetMenuId);
      toast.error(t('admin.copyDay.errorInvalidMenuId', 'Invalid menu ID'));
      setIsCopyDayModalOpen(false);
      return;
    }
    
    console.log('[MenuEditor] Updating React Query cache for menu:', numericTargetMenuId);
    queryClient.setQueryData(['menu', numericTargetMenuId, currentLanguage], transformedMenuData);
    
    // Fix: Add missing cache invalidations to match copy week and other operations
    console.log('[MenuEditor] Invalidating related caches to ensure consistency');
    
    // Invalidate menus list cache (this was the missing piece!)
    queryClient.invalidateQueries(['menus', currentSchoolInstance?.id, currentLanguage]);
    queryClient.invalidateQueries(['menus']); // Also invalidate all menus queries
    
    // Invalidate public menu cache to ensure public-facing menus are updated
    queryClient.invalidateQueries({
      predicate: (query) => 
        query.queryKey[0] === 'publicMenu' && 
        (query.queryKey[1] === currentSchoolInstance?.subdomain || !query.queryKey[1])
    });
    
    // Invalidate available menu types cache
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
    });
    
    // Success notification
    toast.success(t('admin.copyDay.success', 'Day copied successfully'));
    
    // Check if we need to navigate or update in-place
    if (numericTargetMenuId === numericMenuIdFromUrl) {
      console.log(`[MenuEditor] Copy target is current menu. Updating formData in place.`);
      
      // Create a map of target day numbers that received copies from the backend response
      const targetDayUpdates = new Map();
      
      transformedMenuData.days.forEach(backendDay => {
        // Only process days that have menu items (were targets of the copy)
        if (backendDay.items && backendDay.items.length > 0) {
          targetDayUpdates.set(backendDay.dayNumber, backendDay);
        }
      });
      
      console.log('[MenuEditor] Days to update from copy operation:', Array.from(targetDayUpdates.keys()));
      
      // Update form data by only modifying the target days
      setFormData(prevFormData => {
        const updatedDays = prevFormData.days.map(existingDay => {
          if (targetDayUpdates.has(existingDay.dayNumber)) {
            const backendDay = targetDayUpdates.get(existingDay.dayNumber);
            console.log(`[MenuEditor] Updating day ${existingDay.dayNumber} with copied data`);
            
            // Create a map of copied items by courseId for quick lookup
            const copiedItemsByCourse = new Map();
            backendDay.items.forEach(item => {
              copiedItemsByCourse.set(item.courseId, item);
            });
            
            // Ensure all courses are represented - merge copied items with existing course structure
            const allCourseItems = existingDay.items.map(existingItem => {
              if (copiedItemsByCourse.has(existingItem.courseId)) {
                // Use the copied item for this course
                const copiedItem = copiedItemsByCourse.get(existingItem.courseId);
                const finalItem = {
                  ...copiedItem,
                  // Ensure the item is editable by setting has_requested_translation to true for new items
                  // Use snake_case field name that the calendar rendering expects
                  has_requested_translation: copiedItem.hasRequestedTranslation !== false, // Default to true if not explicitly false
                };
                console.log(`[CopyDebug] Day ${existingDay.dayNumber}, Course ${existingItem.courseId}: Original hasRequestedTranslation=${copiedItem.hasRequestedTranslation}, Final has_requested_translation=${finalItem.has_requested_translation}`);
                return finalItem;
              } else {
                // Keep the existing empty item structure for courses that weren't copied
                return existingItem;
              }
            });
            
            // Create a new day object that merges existing properties with copied data
            return {
              ...existingDay, // Preserve all existing frontend properties
              // Update with merged items (copied + empty) and special name from the copy operation
              items: allCourseItems,
              specialName: backendDay.specialName,
              specialNameId: backendDay.specialNameId,
              hasSpecialNameRequestedTranslation: backendDay.hasSpecialNameRequestedTranslation
            };
          }
          
          // Return the existing day unchanged if it wasn't a target of the copy
          return existingDay;
        });
        
        return {
          ...prevFormData, // Preserve all other form data including the source day
          days: updatedDays,
          timestamp: Date.now() // Update timestamp for cache busting
        };
      });
      
      // DO NOT update originalFormData or reset hasChanges
      // This preserves the user's current editing state including unsaved changes
      console.log('[MenuEditor] Form data updated while preserving current editing state');
      
    } else {
      console.log(`[MenuEditor] Copy target is a different menu (target: ${numericTargetMenuId}, current URL menuId: ${numericMenuIdFromUrl}). Navigating.`);
      
      if (numericMenuIdFromUrl) {
        queryClient.invalidateQueries(['menu', numericMenuIdFromUrl, currentLanguage]);
      }
      
      navigate(getMenuEditorUrl(currentSchoolInstance?.subdomain, numericTargetMenuId));
    }
    
    setIsCopyDayModalOpen(false);
  }

  // *** END: Notes and Comments Modal Handlers ***

  return (
    <>
      <style>{notificationStyle}</style>
      <Navbar isAdmin={true} />
      
      <div className="max-w-7xl mx-auto p-4 relative">
        {/* Pink guidance banner for menu creation - only show when no menus exist and not editing */}
        {!isMenusLoading && !isEditing && menusData && menusData.length === 0 && (
          <div className="bg-pink-50 border-l-4 border-pink-500 p-4 mb-6 rounded shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-pink-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-pink-800">
                  {t('admin.guidanceMenuCreation.title', 'Welcome to Menu Management!')}
                </h3>
                <div className="mt-2 text-sm text-pink-700">
                  <p>
                    {t('admin.guidanceMenuCreation.description', 'Use this interface to create monthly menus. You can then choose to display these weekly or monthly in the settings page. Click the create button in the top left of the screen to create your first menu, and remember to save it every time you update it or your changes will be lost. Once you have a menu created, you can preview it in the preview page and adjust how it will be displayed to your community in the settings page. Happy menu management!')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Success notification - positioned as overlay */}
        {success && isNotificationVisible && (
          <div 
            className="fixed top-24 right-4 z-50 w-80 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex justify-between items-center"
            style={{
              animation: `${notificationAnimation} 0.3s ease-in-out`,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <span>{success}</span>
            <button 
              type="button" 
              className="font-bold text-green-700 hover:text-green-800 ml-2" 
              onClick={dismissNotification}
            >
              ×
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h1 className="text-3xl font-bold">
              {isEditing ? t('admin.editMenu') : t('admin.createMenu')}
            </h1>
            
            <div className="flex flex-wrap gap-2 items-center">
              {/* Month selector */}
              <input
                type="month"
                name="month"
                value={formData.month}
                onChange={handleChange}
                className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              
              {/* Menu type selector */}
              <select
                name="menuTypeId"
                value={formData.menuTypeId || ''}
                onChange={(e) => {
                  // Explicitly parse the value to a number before passing to handleChange
                  const numericValue = parseInt(e.target.value, 10);
                  console.log(`[MenuTypeDebug] Menu type dropdown changed: ${e.target.value} => parsed to ${numericValue}`);
                  
                  if (isEditing) {
                    // In edit mode, use the dedicated handler
                    handleMenuTypeChange(numericValue);
                  } else {
                    // In create mode, use the regular handleChange
                    const syntheticEvent = {
                      target: {
                        name: 'menuTypeId',
                        value: numericValue
                      }
                    };
                    handleChange(syntheticEvent);
                  }
                }}
                className={`px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMenuTypesLoading ? 'opacity-75' : ''}`}
                disabled={isMenuTypesLoading}
                required
              >
                {console.log('[MenuTypeDebug] Rendering menu type dropdown:')}
                {console.log('- Current menuTypeId:', formData.menuTypeId)}
                {console.log('- Available menu types:', menuTypes)}
                {console.log('- Loading state:', isMenuTypesLoading)}
                
                {isMenuTypesLoading ? (
                  <option value="" disabled>
                    {t('admin.loading', 'Loading...')}
                  </option>
                ) : (
                  <>
                    {menuTypes && menuTypes.length > 0 ? (
                      menuTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.translated_name || type.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        {t('admin.noMenuTypesAvailable', 'No menu types available')}
                      </option>
                    )}
                  </>
                )}
              </select>
              
              {/* Preview button - only show when editing an existing menu */}
              {isEditing && (
                <button
                  type="button"
                  className="px-4 py-2 rounded font-medium transition-colors"
                  style={{ 
                    backgroundColor: school?.secondary_bg_color || '#3498DB', 
                    color: school?.secondary_text_color || '#FFFFFF'
                  }}
                  onClick={() => navigate(`${getAdminUrl(school?.subdomain)}/preview/${menuId}`)}
                >
                  {t('admin.preview')}
                </button>
              )}
              
              {/* Copy button */}
              <button
                type="button"
                className="px-4 py-2 bg-gray-700 text-white rounded-md shadow-sm hover:bg-gray-600"
                onClick={handleCopyMenu}
              >
                {t('admin.copy')}
              </button>
              
              {/* Delete button - only show when editing */}
              {isEditing && (
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  onClick={handleDeleteMenu}
                >
                  {t('admin.deleteMenu')}
                </button>
              )}
              
              {/* Primary action button moved to top */}
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded font-medium transition-colors"
                  style={{ 
                    backgroundColor: school?.primary_bg_color || '#2C3E50', 
                    color: school?.primary_text_color || '#FFFFFF'
                  }}
                >
                  {saving ? t('general.saving') : (isEditing ? t('general.save') : t('general.create'))}
                </button>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {/* Warning when no courses are attached to menu type */}
          {courses.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
              <h3 className="font-semibold">{t('admin.noCourses', 'No Courses Available')}</h3>
              <p>{t('admin.noCoursesMessage', 'This menu type has no courses attached to it. Please configure courses for this menu type in the Settings page before creating a menu.')}</p>
            </div>
          )}
          
          {/* Calendar-style menu editor */}
          <div className="bg-white border rounded-md shadow-sm overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="border p-2 bg-gray-100">{t('calendar.monday')}</th>
                  <th className="border p-2 bg-gray-100">{t('calendar.tuesday')}</th>
                  <th className="border p-2 bg-gray-100">{t('calendar.wednesday')}</th>
                  <th className="border p-2 bg-gray-100">{t('calendar.thursday')}</th>
                  <th className="border p-2 bg-gray-100">{t('calendar.friday')}</th>
                </tr>
              </thead>
              <tbody>
                {calendar.map((week, weekIndex) => (
                  <tr key={`week-${weekIndex}`}>
                    {week.slice(0, 5).map((day, dayIndex) => (
                      <td key={`day-${weekIndex}-${dayIndex}`} className="border p-0 align-top">
                        {day ? (
                          <div className="flex flex-col h-full">
                            {/* Day number and special name */}
                            <div className="py-2 pl-2 border-b flex items-center">
                              <span className="font-bold min-w-[1.5rem]">{day.dayNumber}</span>
                              <div 
                                ref={el => specialNameAutocompleteRefs.current[`day-${day.dayNumber}`] = el}
                                className="relative w-full"
                              >
                                {(() => { // Wrap logic in IIFE for clarity
                                  // Determine disabled state based on data from GET /admin/menus/:id response
                                  const rawFlag = day.has_special_name_requested_translation;
                                  const hasRequestedTranslation = rawFlag ?? false; // Default to false if missing
                                  const isDisabled = day.specialNameId && !hasRequestedTranslation;
                                  const displayValue = day.specialName || (isDisabled ? t('translation.missing', 'Translation Missing') : '');

                                  // --- Add Detailed Logging ---
                                  console.log(`[DisableDebug-Special] Day ${day.dayNumber}: ID=${day.specialNameId}, RawFlag=${rawFlag}, hasRequested=${hasRequestedTranslation}, isDisabled=${isDisabled}, Value='${day.specialName}'`);
                                  // --- End Detailed Logging ---

                                  return (
                                    <div className={`relative ${isDisabled ? 'group' : ''}`}>
                                      <textarea
                                        rows="1"
                                        value={displayValue} // Use calculated display value
                                        readOnly={isDisabled} // Set readOnly based on disabled state
                                        disabled={isDisabled} // Set disabled attribute
                                        onChange={(e) => !isDisabled && handleDayChange(day.dayNumber, e.target.value)}
                                        onFocus={() => !isDisabled && handleSpecialNameFocus(day.dayNumber)}
                                        onKeyDown={(e) => !isDisabled && handleSpecialNameKeyDown(e, day.dayNumber)}
                                        placeholder={t('admin.specialDayPlaceholder')}
                                        className={`w-full text-sm px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none overflow-hidden block min-h-[36px] leading-[25px] ${isDisabled
                                          ? 'bg-gray-100 text-gray-600 italic border-l-4 border-l-yellow-400 cursor-pointer'
                                          : '' // Standard input styling if not disabled
                                        }`}
                                        ref={el => {
                                          // Prevent scrolling and focus issues by using this approach
                                          preventAutoScroll(() => {
                                            specialNameInputRefs.current[`day-${day.dayNumber}`] = el;
                                          });
                                        }}
                                      />
                                      {isDisabled && (
                                        // Overlay button only when disabled
                                        <button
                                          type="button"
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setModalState({
                                              isOpen: true,
                                              dayNumber: day.dayNumber,
                                              courseId: null,
                                              dayId: day.id || null,
                                              isSpecialName: true
                                            });
                                          }}
                                          aria-label={t('modal.changeUntranslatedSpecialName')}
                                        />
                                      )}
                                    </div>
                                  );
                                })()}
                                
                                {/* Special name autocomplete dropdown */}
                                {showSpecialNameAutocomplete[`day-${day.dayNumber}`] && (
                                  <div 
                                    ref={el => {
                                      // Store in ref
                                      specialNameDropdownRefs.current[`day-${day.dayNumber}`] = el;
                                      
                                      // Scroll to bottom if it's a top-positioned dropdown
                                      if (el && dropdownPositions[`day-${day.dayNumber}`] === 'top') {
                                        // Use requestAnimationFrame to ensure this happens after render
                                        requestAnimationFrame(() => {
                                          el.scrollTop = el.scrollHeight;
                                        });
                                      }
                                    }}
                                    className={`absolute z-10 ${
                                      dropdownPositions[`day-${day.dayNumber}`] === 'top' 
                                        ? 'bottom-full mb-1' 
                                        : 'top-full mt-1'
                                    } w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto`}
                                  >
                                    {/* For top-positioned dropdowns, reverse the order of items */}
                                    {dropdownPositions[`day-${day.dayNumber}`] === 'top' ? (
                                      <>
                                        {specialNameAutocompleteResults[`day-${day.dayNumber}`]?.length > 0 ? (
                                          <>
                                            {[...specialNameAutocompleteResults[`day-${day.dayNumber}`]].reverse().map((specialName, index) => {
                                              // Adjust index for reversed list
                                              const totalItems = specialNameAutocompleteResults[`day-${day.dayNumber}`].length;
                                              const reversedIndex = totalItems - index - 1;
                                              
                                              // Calculate the index value based on whether the create option exists
                                              const hasExactMatch = specialNameAutocompleteResults[`day-${day.dayNumber}`]?.some(s => 
                                                s.name && day?.specialName && 
                                                s.name.toLowerCase() === day.specialName.toLowerCase()
                                              );
                                              // If we have an exact match, there's no create option, so indices start at 0
                                              // If no exact match, create option has index 0, so results start at index 1
                                              const actualIndex = hasExactMatch ? reversedIndex : reversedIndex + 1;
                                              return (
                                                <div
                                                  key={specialName.id}
                                                  className={`px-4 py-2 cursor-pointer ${
                                                    focusedSpecialNameIndex[`day-${day.dayNumber}`] === actualIndex
                                                      ? 'bg-blue-100' 
                                                      : 'hover:bg-gray-100'
                                                  }`}
                                                  onClick={() => handleSpecialNameAutocompleteSelect(day.dayNumber, specialName)}
                                                >
                                                  {specialName.name}
                                                </div>
                                              );
                                            })}
                                          </>
                                        ) : (
                                          <div className="px-4 py-2 text-gray-500">
                                            {t('admin.noMatchingSpecialNames')}
                                          </div>
                                        )}
                                        
                                        {/* Only show create option if there isn't an exact match for the special name */}
                                        {!specialNameAutocompleteResults[`day-${day.dayNumber}`]?.some(specialName => 
                                          specialName.name && day.specialName && 
                                          specialName.name.toLowerCase() === day.specialName.toLowerCase()
                                        ) && day.specialName && day.specialName.trim() && (
                                          <div 
                                            className={`px-4 py-2 border-t cursor-pointer ${
                                              focusedSpecialNameIndex[`day-${day.dayNumber}`] === 0
                                                ? 'bg-blue-200 text-blue-800' 
                                                : 'hover:bg-blue-50 text-blue-600'
                                            }`}
                                            onClick={() => handleCreateNewSpecialName(day.dayNumber, day.specialName)}
                                          >
                                            + {t('admin.createNewSpecialName')}: {day.specialName}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      // Original order for bottom-positioned dropdowns
                                      <>
                                    {/* Only show create option if there isn't an exact match for the special name */}
                                    {!specialNameAutocompleteResults[`day-${day.dayNumber}`]?.some(specialName => 
                                      specialName.name && day.specialName && 
                                      specialName.name.toLowerCase() === day.specialName.toLowerCase()
                                    ) && day.specialName && day.specialName.trim() && (
                                      <div 
                                        className={`px-4 py-2 border-b cursor-pointer ${
                                          focusedSpecialNameIndex[`day-${day.dayNumber}`] === 0
                                            ? 'bg-blue-200 text-blue-800' 
                                            : 'hover:bg-blue-50 text-blue-600'
                                        }`}
                                        onClick={() => handleCreateNewSpecialName(day.dayNumber, day.specialName)}
                                      >
                                        + {t('admin.createNewSpecialName')}: {day.specialName}
                                      </div>
                                    )}
                                    
                                    {specialNameAutocompleteResults[`day-${day.dayNumber}`]?.length > 0 ? (
                                      <>
                                        {specialNameAutocompleteResults[`day-${day.dayNumber}`].map((specialName, index) => {
                                          // Calculate the index value based on whether the create option exists
                                          const hasExactMatch = specialNameAutocompleteResults[`day-${day.dayNumber}`]?.some(s => 
                                            s.name && day?.specialName && 
                                            s.name.toLowerCase() === day.specialName.toLowerCase()
                                          );
                                          // If we have an exact match, there's no create option, so indices start at 0
                                          // If no exact match, create option has index 0, so results start at index 1
                                          const actualIndex = hasExactMatch ? index : index + 1;
                                          return (
                                            <div
                                              key={specialName.id}
                                              className={`px-4 py-2 cursor-pointer ${
                                                focusedSpecialNameIndex[`day-${day.dayNumber}`] === actualIndex
                                                  ? 'bg-blue-100' 
                                                  : 'hover:bg-gray-100'
                                              }`}
                                              onClick={() => handleSpecialNameAutocompleteSelect(day.dayNumber, specialName)}
                                            >
                                              {specialName.name}
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <div className="px-4 py-2 text-gray-500">
                                        {t('admin.noMatchingSpecialNames')}
                                      </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Three Dots Dropdown for Notes and Comments - Step 6 */}
                              {isEditing && (
                                <div className="mr-1 relative">
                                  {/* Unread comments indicator - Step 7 */}
                                  {day.has_unread_comments && (
                                    <div className="absolute -top-2 -right-0.5 w-2.5 h-2.5 bg-orange-400 rounded-full shadow-sm"></div>
                                  )}
                                  <ThreeDotsDropdown 
                                    onNotesClick={() => handleOpenNotesModal(day.id)}
                                    onCommentsClick={() => handleOpenCommentsModal(day.id)}
                                    onCopyWeekClick={() => handleOpenCopyWeekModal(day.dayNumber)}
                                    onCopyDayClick={() => handleOpenCopyDayModal(day.dayNumber)}
                                    menuId={menuId}
                                  />
                                </div>
                              )}
                              
                              
                            </div>
                            
                            {/* Courses */}
                            {courses.length > 0 && day.items.map(item => {
                              const course = courses.find(c => c.id === item.courseId);
                              if (!course) return null;
                              
                              const key = `${day.dayNumber}-${course.id}`;
                              
                              // Determine disabled state based on data from GET /admin/menus/:id response
                              const rawFlag = item.has_requested_translation;
                              const hasRequestedTranslation = rawFlag ?? false; // Default to false if undefined
                              const isDisabled = item.dishId && !hasRequestedTranslation; // Disabled if ID exists but flag is false
                              const displayValue = item.dishName || (isDisabled ? t('translation.missing', 'Translation Missing') : '');

                              // --- Add Detailed Logging ---
                              console.log(`[DisableDebug-Dish] Day ${day.dayNumber}, Course ${course.id}: ID=${item.dishId}, RawFlag=${rawFlag}, hasRequested=${hasRequestedTranslation}, isDisabled=${isDisabled}, Value='${item.dishName}'`);
                              // --- End Detailed Logging ---

                              return (
                                <div 
                                  key={`course-${day.dayNumber}-${course.id}`}
                                  className="relative border-b last:border-b-0"
                                >
                                  <div 
                                    ref={el => autocompleteRefs.current[key] = el}
                                    className={`relative ${getCourseColor(course)} ${isDisabled ? 'group' : ''}`}
                                  >
                                    <textarea
                                      rows="1"
                                      value={displayValue} // Use calculated display value
                                      readOnly={isDisabled} // Set readOnly based on disabled state
                                      disabled={isDisabled} // Set disabled attribute
                                      onChange={(e) => !isDisabled && handleDishInputChange(day.dayNumber, course.id, e.target.value)}
                                      onFocus={() => !isDisabled && handleDishFocus(day.dayNumber, course.id)}
                                      onKeyDown={(e) => !isDisabled && handleDishKeyDown(e, day.dayNumber, course.id)}
                                      placeholder={course.name}
                                      className={`w-full text-sm px-3 py-3 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none block ${isDisabled
                                        ? 'text-gray-600 italic border-l-4 border-l-yellow-400 cursor-pointer'
                                        : '' // Standard input styling if not disabled
                                      }`}
                                      ref={el => dishInputRefs.current[key] = el}
                                    />
                                    {isDisabled && (
                                      // Overlay button only when disabled
                                      <button
                                        type="button"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        onClick={(e) => { // Trigger modal on click
                                           e.stopPropagation();
                                           setModalState({
                                             isOpen: true,
                                             dayNumber: day.dayNumber,
                                             courseId: course.id,
                                             dayId: day.id || null,
                                             isSpecialName: false
                                           });
                                        }}
                                        aria-label={t('modal.changeUntranslatedDish')}
                                      />
                                    )}

                                    {/* Autocomplete dropdown */}
                                    {showAutocomplete[key] && (
                                      <div 
                                        ref={el => {
                                          // Store in ref
                                          dishDropdownRefs.current[key] = el;
                                          
                                          // Scroll to bottom if it's a top-positioned dropdown
                                          if (el && dropdownPositions[key] === 'top') {
                                            // Use requestAnimationFrame to ensure this happens after render
                                            requestAnimationFrame(() => {
                                              el.scrollTop = el.scrollHeight;
                                            });
                                          }
                                        }}
                                        className={`absolute z-10 ${
                                          dropdownPositions[key] === 'top' 
                                            ? 'bottom-full mb-1' 
                                            : 'top-full mt-1'
                                        } w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto`}
                                      >
                                        {/* For top-positioned dropdowns, reverse the order of items */}
                                        {dropdownPositions[key] === 'top' ? (
                                          <>
                                            {autocompleteResults[key]?.length > 0 ? (
                                              <>
                                                {[...autocompleteResults[key]].reverse().map((dish, index) => {
                                                  // Adjust index for reversed list
                                                  const totalItems = autocompleteResults[key].length;
                                                  const reversedIndex = totalItems - index - 1;
                                                  
                                                  // Calculate the index value based on whether the create option exists
                                                  const hasExactMatch = autocompleteResults[key]?.some(d => 
                                                    d.name && item?.dishName && 
                                                    d.name.toLowerCase() === item.dishName.toLowerCase()
                                                  );
                                                  // If we have an exact match, there's no create option, so indices start at 0
                                                  // If no exact match, create option has index 0, so results start at index 1
                                                  const actualIndex = hasExactMatch ? reversedIndex : reversedIndex + 1;
                                                  return (
                                                    <div
                                                      key={dish.id}
                                                      className={`px-4 py-2 cursor-pointer ${
                                                        focusedDishIndex[key] === actualIndex 
                                                          ? 'bg-blue-100' 
                                                          : 'hover:bg-gray-100'
                                                      }`}
                                                      onClick={() => handleAutocompleteSelect(day.dayNumber, course.id, dish)}
                                                    >
                                                      {dish.name}
                                                    </div>
                                                  );
                                                })}
                                              </>
                                            ) : (
                                              <div className="px-4 py-2 text-gray-500">
                                                {t('admin.noMatchingDishes')}
                                              </div>
                                            )}
                                            
                                            {/* Only show create option if there isn't an exact match for the dish name */}
                                            {!autocompleteResults[key]?.some(dish => 
                                              dish.name && item.dishName?.toLowerCase() === dish.name.toLowerCase()
                                            ) && item.dishName && item.dishName.trim() && (
                                              <div 
                                                className={`px-4 py-2 border-t cursor-pointer ${
                                                  focusedDishIndex[key] === 0
                                                    ? 'bg-blue-200 text-blue-800' 
                                                    : 'hover:bg-blue-50 text-blue-600'
                                                }`}
                                                onClick={() => handleCreateNewDish(day.dayNumber, course.id, item.dishName)}
                                              >
                                                + {t('admin.createNewDish')}: {item.dishName}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          // Original order for bottom-positioned dropdowns
                                          <>
                                        {/* Only show create option if there isn't an exact match for the dish name */}
                                        {!autocompleteResults[key]?.some(dish => 
                                          dish.name && item.dishName?.toLowerCase() === dish.name.toLowerCase()
                                        ) && item.dishName && item.dishName.trim() && (
                                          <div 
                                            className={`px-4 py-2 border-b cursor-pointer ${
                                              focusedDishIndex[key] === 0
                                                ? 'bg-blue-200 text-blue-800' 
                                                : 'hover:bg-blue-50 text-blue-600'
                                            }`}
                                            onClick={() => handleCreateNewDish(day.dayNumber, course.id, item.dishName)}
                                          >
                                            + {t('admin.createNewDish')}: {item.dishName}
                                          </div>
                                        )}
                                        
                                        {autocompleteResults[key]?.length > 0 ? (
                                          <>
                                            {autocompleteResults[key].map((dish, index) => {
                                              // Calculate the index value based on whether the create option exists
                                              const hasExactMatch = autocompleteResults[key]?.some(d => 
                                                d.name && item?.dishName && 
                                                d.name.toLowerCase() === item.dishName.toLowerCase()
                                              );
                                              // If we have an exact match, there's no create option, so indices start at 0
                                              // If no exact match, create option has index 0, so results start at index 1
                                              const actualIndex = hasExactMatch ? index : index + 1;
                                              return (
                                                <div
                                                  key={dish.id}
                                                  className={`px-4 py-2 cursor-pointer ${
                                                    focusedDishIndex[key] === actualIndex 
                                                      ? 'bg-blue-100' 
                                                      : 'hover:bg-gray-100'
                                                  }`}
                                                  onClick={() => handleAutocompleteSelect(day.dayNumber, course.id, dish)}
                                                >
                                                  {dish.name}
                                                </div>
                                              );
                                            })}
                                          </>
                                        ) : (
                                          <div className="px-4 py-2 text-gray-500">
                                            {t('admin.noMatchingDishes')}
                                          </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="h-full bg-gray-50"></div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </form>
        
        {/* Modal for untranslated dish confirmation */}
        <ConfirmationModal
          isOpen={modalState.isOpen}
          title={modalState.isSpecialName ? t('modal.changeUntranslatedSpecialName') : t('modal.changeUntranslatedDish')}
          message={modalState.isSpecialName 
            ? t('modal.untranslatedSpecialName', { language: i18n.language })
            : t('modal.untranslatedDish', { language: i18n.language })}
          onConfirm={() => {
            handleConfirmDishChange();
          }}
          onCancel={() => {
            handleCancelDishChange();
          }}
          onClose={() => {
            handleCancelDishChange();
          }}
        />
      </div>
      
      {/* Add the DeleteMenuModal component */}
      <DeleteMenuModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
        menuId={menuId}
      />
      
      {/* Add the Copy Menu Modal */}
      <CopyMenuModal 
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        isEditing={isEditing}
        currentMonth={formData.month}
        currentMenuTypeId={formData.menuTypeId}
        school={school}
        onCopyComplete={handleCopyComplete}
        schoolId={getSchoolId()}
      />
      
      {/* Re-enable Notes and Comments Modals - Step 4 */}
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={handleCloseNotesModal}
        dayId={selectedDayIdForModal}
        initialNoteText={currentNoteText}
        onSave={handleSaveNote}
      />

      <CommentsModal
        isOpen={isCommentsModalOpen}
        onClose={handleCloseCommentsModal}
        dayId={selectedDayIdForModal}
        menuId={menuId} // Pass menuId here as well
      />
      
      {/* Add the Copy Week Modal */}
      <CopyWeekModal
        isOpen={isCopyWeekModalOpen}
        onClose={() => setIsCopyWeekModalOpen(false)}
        sourceWeekDays={sourceWeekDays}
        sourceMenuId={menuId}
        sourceMenuTypeId={formData.menuTypeId}
        onCopyComplete={handleCopyWeekComplete}
      />
      
      {/* Add the Copy Day Modal */}
      <CopyDayModal
        isOpen={isCopyDayModalOpen}
        onClose={() => setIsCopyDayModalOpen(false)}
        sourceDayData={sourceDayData}
        sourceMenuId={menuId}
        sourceMenuTypeId={formData.menuTypeId}
        onCopyComplete={handleCopyDayComplete}
      />
    </>
  );
};

export default MenuEditor; 